// src/pages/CustomerDetailsPage.jsx - REVISED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { redeemVoucher } from '../api/salonApi';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  getDoc,
  increment,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { canShareSlot, rangesOverlap } from '../utils/multiServiceAvailability';
import { getSalonConfig } from '../api/salonConfigService';
import { useSalonLocations } from '../hooks/useSalonLocations';
import { getPayNowConfig, generatePayNowReference, generateDynamicQR, cancelPayNowTransaction } from '../api/paynowService';
import {
  calculatePointsLast365Days,
  determineLoyaltyTier,
  getLoyaltyTierSettings,
  updateCustomerTotalSpent,
  updateCustomerTotalSpentLast365Days,
  updateCustomerLoyaltyTier
} from '../utils/updateCustomerMetrics';
import { sendTransactionalPushNotification, notifyWalletDebit, notifyWalletTopup } from '../utils/bookingNotifications';
import whatsappService from '../api/whatsappService';
import {
  loadSalonNotificationConfig, resolveEmailTemplate, resolvePushTemplate, renderTemplate,
  formatMoney, makeInvoiceNumber, fetchPointsBalance, fetchWalletBalance
} from '../utils/transactionalTemplates';
import { verifyTopup, rejectTopup } from '../utils/walletTopupActions';
import PayNowQRModal from '../components/admin/PayNowQRModal';
import '../styles/customerdetails.css';
import '../styles/paynow-qr-modal.css';
import '../styles/responsive-utils.css';
import '../styles/customerdetails-responsive.css';

function CustomerDetailsPage() {
  const { salonId, customerId } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  
  const [activeTab, setActiveTab] = useState('appointments');
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Customer data
  const [customer, setCustomer] = useState(null);

  // Tab data states
  const [appointments, setAppointments] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [walletActionLoadingId, setWalletActionLoadingId] = useState(null);
  const vouchersUnsubscribeRef = useRef(null);
  const isAddingTransactionRef = useRef(false);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);

  // #Revised UI_v2 — prevent duplicate notification sends from rapid double-click
  const isUpdatingTransactionRef = useRef(false);

  // Modal states
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showEditTransaction, setShowEditTransaction] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showAddAppointment, setShowAddAppointment] = useState(false);

  // Product transaction modal states
  const [showTransactionTypeChoice, setShowTransactionTypeChoice] = useState(false);
  const [showAddProductTransaction, setShowAddProductTransaction] = useState(false);
  const [showAddPackageTransaction, setShowAddPackageTransaction] = useState(false);
  const [packageTransactionForm, setPackageTransactionForm] = useState({
    packageName: '',
    quantity: 1,
    staffId: '',
    customPrice: '',
    paymentMethod: 'cash',
    paymentStatus: 'Pending',
    locationId: ''
  });
  const isAddingPackageTransactionRef = useRef(false);
  const [products, setProducts] = useState([]);
  const [productTransactionForm, setProductTransactionForm] = useState({
    productId: '',
    quantity: 1,
    staffId: '',
    customPrice: '',
    paymentMethod: 'cash',
    paymentStatus: 'Pending',
    locationId: ''
  });
  // Tracks product context for a pending PayNow transaction so stock can be
  // decremented when the QR payment is confirmed.
  const [pendingProductId, setPendingProductId] = useState(null);
  const [pendingProductQuantity, setPendingProductQuantity] = useState(1);
  const isAddingProductTransactionRef = useRef(false);

  // PayNow states
  const [showPayNowQR, setShowPayNowQR] = useState(false);
  const [paynowConfig, setPaynowConfig] = useState(null);
  const [paynowReference, setPaynowReference] = useState(null);
  const [pendingTransactionId, setPendingTransactionId] = useState(null);
  const [paynowAmount, setPaynowAmount] = useState(0);
  const [paynowQRData, setPaynowQRData] = useState(null);
  const [paynowIsDynamic, setPaynowIsDynamic] = useState(false);
  const [paynowFallback, setPaynowFallback] = useState(false);
  const [paynowFallbackReason, setPaynowFallbackReason] = useState('');
  const [paynowIsGenerating, setPaynowIsGenerating] = useState(false);

  // Loyalty program state
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);

  // Package History state
  const [customerPackages, setCustomerPackages] = useState([]); // current state of each package (for dropdowns + header)
  const [packageHistory, setPackageHistory] = useState([]);    // immutable action log shown in the history table
  const [showAddPackageAction, setShowAddPackageAction] = useState(false);
  const [packageActionForm, setPackageActionForm] = useState({ packageId: '', action: '' });
  const [packageActionSaving, setPackageActionSaving] = useState(false);
  const [salonServicePackages, setSalonServicePackages] = useState([]); // configured packages from salons doc

  // Voucher redemption state (populated when admin arrives via QR scan)
  const [activeVoucher, setActiveVoucher]     = useState(null);  // the voucher being redeemed
  const [voucherApplied, setVoucherApplied]   = useState(false); // discount has been calculated
  const [nettPrice, setNettPrice]             = useState(null);  // post-discount price
  
  // Form states
  const [transactionForm, setTransactionForm] = useState({
    serviceId: '',
    staffId: '',
    customPrice: '',
    paymentMethod: 'cash',
    paymentStatus: 'Pending',
    locationId: ''
  });

  const [appointmentForm, setAppointmentForm] = useState({
    dateRequested: '',
    timeRequested: '',
    staffId: '',
    serviceId: '',
    packageId: '',   // customerPackages doc id when booking by package
    appointmentType: 'service', // 'service' | 'package'
    notes: '',
    locationId: ''
  });

  // Business hours and availability states
  const [businessHours, setBusinessHours] = useState([]);
  const [holidayClosures, setHolidayClosures] = useState([]);
  const [customClosures, setCustomClosures] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [validationMessage, setValidationMessage] = useState('');
  const [filteredServices, setFilteredServices] = useState([]);

  // Debug logging
  useEffect(() => {
    console.log('CustomerDetailsPage mounted with params:', { salonId, customerId });
    console.log('Current state:', { 
      activeTab, 
      appointmentsCount: appointments.length, 
      vouchersCount: vouchers.length, 
      transactionsCount: transactions.length 
    });
  }, [salonId, customerId, activeTab, appointments.length, vouchers.length, transactions.length]);

  // Load customer data on mount
  useEffect(() => {
    if (customerId && salonId) {
      loadCustomerData();
      loadPayNowConfig();
      loadLoyaltyConfig();
    }
  }, [customerId, salonId]);

  // Real-time listener: customer's packages
  // Only filter by customerId — it is already salon-scoped (email_salonId),
  // so a compound index is not needed and the query works without one.
  useEffect(() => {
    if (!customerId) return;
    const q = query(
      collection(db, 'customerPackages'),
      where('customerId', '==', customerId)
    );
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setCustomerPackages(data);
    }, err => {
      console.error('customerPackages listener error:', err);
      setError('Failed to load package history: ' + err.message);
    });
    return unsub;
  }, [customerId]);

  // Real-time listener: action log for the Package History tab
  useEffect(() => {
    if (!customerId) return;
    const q = query(
      collection(db, 'customerPackageHistory'),
      where('customerId', '==', customerId)
    );
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.performedAt?.seconds || 0) - (a.performedAt?.seconds || 0));
      setPackageHistory(data);
    }, err => {
      console.error('customerPackageHistory listener error:', err);
    });
    return unsub;
  }, [customerId]);

  // Load salon-configured service packages (for Add Package modal dropdown)
  useEffect(() => {
    if (!salonId) return;
    getDoc(doc(db, 'salons', salonId)).then(snap => {
      if (snap.exists()) {
        setSalonServicePackages(snap.data().servicePackages || []);
      }
    }).catch(err => console.error('Error loading salon service packages:', err));
  }, [salonId]);

  // Cleanup vouchers real-time listener on unmount
  useEffect(() => {
    return () => {
      if (vouchersUnsubscribeRef.current) {
        vouchersUnsubscribeRef.current();
      }
    };
  }, []);

  // Auto-open transaction modal when admin arrives via QR scan
  useEffect(() => {
    if (location.state?.pendingVoucher) {
      setActiveVoucher(location.state.pendingVoucher);
      setVoucherApplied(false);
      setNettPrice(null);
      setShowAddTransaction(true);
      // Clear router state so a page refresh doesn't re-trigger
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.state]);

  // Load PayNow configuration
  const loadPayNowConfig = async () => {
    try {
      const config = await getPayNowConfig(salonId);
      setPaynowConfig(config);
    } catch (error) {
      console.log('PayNow not configured for this salon:', error);
      setPaynowConfig(null);
    }
  };

  // Load loyalty program configuration
  const loadLoyaltyConfig = async () => {
    try {
      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);
      if (salonDoc.exists()) {
        const data = salonDoc.data();
        setLoyaltyConfig(data.loyaltyProgram || null);
      }
    } catch (error) {
      console.log('Loyalty program not configured for this salon:', error);
      setLoyaltyConfig(null);
    }
  };

  // Handle package actions: Add Package, Delete Package, Add Session, Deplete Session
  const handlePackageAction = async () => {
    const { packageId, action } = packageActionForm;
    if (!packageId || !action) return;
    setPackageActionSaving(true);
    try {
      const selectedSalonPkg = salonServicePackages.find(p => p.name === packageId);

      const logAction = async (packageDocId, packageName, actionLabel, totalSessions, sessionsUsed) => {
        await addDoc(collection(db, 'customerPackageHistory'), {
          customerId,
          salonId,
          packageDocId,
          packageName,
          action: actionLabel,
          totalSessionsAfter: totalSessions,
          sessionsUsedAfter: sessionsUsed,
          sessionsLeftAfter: Math.max(0, totalSessions - sessionsUsed),
          performedAt: new Date(),
        });
      };

      if (action === 'Add Package') {
        if (!selectedSalonPkg) throw new Error('Package definition not found.');
        const total = selectedSalonPkg.totalSessions || 0;
        const pkgRef = await addDoc(collection(db, 'customerPackages'), {
          customerId,
          salonId,
          packageName: selectedSalonPkg.name,
          packageDefinition: selectedSalonPkg,
          totalSessions: total,
          paidSessions: selectedSalonPkg.paidSessions,
          complimentarySessions: selectedSalonPkg.complimentarySessions,
          sessionsUsed: 0,
          status: 'active',
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        await logAction(pkgRef.id, selectedSalonPkg.name, 'Add Package', total, 0);
      } else if (action === 'Delete Package') {
        const pkgDoc = await getDoc(doc(db, 'customerPackages', packageId));
        if (!pkgDoc.exists()) throw new Error('Package not found.');
        const prev = pkgDoc.data();
        await updateDoc(doc(db, 'customerPackages', packageId), { status: 'deleted', updatedAt: new Date() });
        await logAction(packageId, prev.packageName, 'Delete Package', prev.totalSessions || 0, prev.sessionsUsed || 0);
      } else if (action === 'Add Session') {
        const pkgDoc = await getDoc(doc(db, 'customerPackages', packageId));
        if (!pkgDoc.exists()) throw new Error('Package not found.');
        const prev = pkgDoc.data();
        const newTotal = (prev.totalSessions || 0) + 1;
        await updateDoc(doc(db, 'customerPackages', packageId), { totalSessions: newTotal, updatedAt: new Date() });
        await logAction(packageId, prev.packageName, 'Add Session', newTotal, prev.sessionsUsed || 0);
      } else if (action === 'Deplete Session') {
        const pkgDoc = await getDoc(doc(db, 'customerPackages', packageId));
        if (!pkgDoc.exists()) throw new Error('Package not found.');
        const prev = pkgDoc.data();
        const newUsed = Math.min((prev.sessionsUsed || 0) + 1, prev.totalSessions || 0);
        await updateDoc(doc(db, 'customerPackages', packageId), { sessionsUsed: newUsed, updatedAt: new Date() });
        await logAction(packageId, prev.packageName, 'Deplete Session', prev.totalSessions || 0, newUsed);
      }

      setShowAddPackageAction(false);
      setPackageActionForm({ packageId: '', action: '' });
    } catch (err) {
      console.error('handlePackageAction error:', err);
      setError(err.message || 'Failed to perform package action.');
    } finally {
      setPackageActionSaving(false);
    }
  };

  // Load tab data when active tab changes
  useEffect(() => {
    if (customer && activeTab) {
      console.log(`Loading data for tab: ${activeTab}`);
      loadTabData(activeTab);
    }
  }, [activeTab, customer]);

  // Load business hours
  useEffect(() => {
    const loadBusinessHours = async () => {
      try {
        const config = await getSalonConfig(salonId);
        if (config && config.businessHours) {
          let hoursArray = [];
          let holidays = [];
          let custom = [];
          if (Array.isArray(config.businessHours)) {
            hoursArray = config.businessHours;
          } else {
            hoursArray = config.businessHours.regularHours || [];
            holidays   = config.businessHours.holidayClosures || [];
            custom     = config.businessHours.customClosures  || [];
          }
          setBusinessHours(hoursArray);
          setHolidayClosures(holidays);
          setCustomClosures(custom);
        }
      } catch (error) {
        console.error('Error loading business hours:', error);
      }
    };
    if (salonId) {
      loadBusinessHours();
    }
  }, [salonId]);

  // Generate time slots when date or staff changes
  useEffect(() => {
    if (appointmentForm.dateRequested && appointmentForm.staffId) {
      generateTimeSlots();
    } else {
      setAvailableTimeSlots([]);
      setValidationMessage('');
    }
  }, [appointmentForm.dateRequested, appointmentForm.staffId]);

  // Filter services based on selected staff
  useEffect(() => {
    if (appointmentForm.staffId && staff.length > 0 && services.length > 0) {
      const selectedStaff = staff.find(s => s.id === appointmentForm.staffId);
      if (selectedStaff && selectedStaff.services) {
        // Filter services that the staff offers — staff.services may store the Firestore
        // doc id, a separate serviceId field, or (legacy) the service name, so match any of them.
        const availableServices = services.filter(service => {
          return selectedStaff.services.includes(service.id) ||
            selectedStaff.services.includes(service.serviceId) ||
            selectedStaff.services.includes(service.name);
        });
        console.log(`Filtering services for staff "${selectedStaff.name}":`, availableServices);
        setFilteredServices(availableServices);

        // Clear selected service if staff doesn't offer it
        if (appointmentForm.serviceId) {
          const isCurrentServiceAvailable = availableServices.some(s => s.id === appointmentForm.serviceId);
          if (!isCurrentServiceAvailable) {
            setAppointmentForm(prev => ({ ...prev, serviceId: '' }));
          }
        }
      } else {
        // Show all services if staff has no service restrictions
        setFilteredServices(services);
      }
    } else {
      // Show all services if no staff is selected
      setFilteredServices(services);
    }
  }, [appointmentForm.staffId, staff, services, appointmentForm.serviceId]);

  // Auto-reset payment method if Points becomes unaffordable
  useEffect(() => {
    if (transactionForm.paymentMethod === 'Points' && transactionForm.serviceId) {
      const selectedService = services.find(s => s.id === transactionForm.serviceId);
      if (selectedService) {
        const finalPrice = parseFloat(transactionForm.customPrice) || 0;
        const pointValue = loyaltyConfig?.pointValue || 0.1;
        const pointsRequired = Math.ceil(finalPrice / pointValue);
        const pointsBalance = customer?.metrics?.pointsBalance || 0;
        if (pointsBalance < pointsRequired) {
          setTransactionForm(prev => ({ ...prev, paymentMethod: 'cash' }));
        }
      }
    }
  }, [transactionForm.serviceId, transactionForm.customPrice]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Loading customer data for:', customerId);
      const customerDoc = await getDoc(doc(db, 'customers', customerId));

      if (customerDoc.exists()) {
        const customerData = customerDoc.data();
        console.log('Customer data loaded:', customerData);

        // Calculate loyalty tier based on points earned in last 365 days
        const tierSettings = await getLoyaltyTierSettings(salonId);
        const pointsLast365Days = await calculatePointsLast365Days(
          customerData.email,
          salonId
        );
        const calculatedTier = determineLoyaltyTier(pointsLast365Days, tierSettings);

        // Update the customer document if tier has changed
        if (calculatedTier !== customerData.loyaltyTier) {
          console.log(`Updating loyalty tier: ${customerData.loyaltyTier} -> ${calculatedTier} (${pointsLast365Days} points)`);
          const customerRef = doc(db, 'customers', customerId);
          await updateDoc(customerRef, {
            loyaltyTier: calculatedTier,
            'metrics.pointsLast365Days': pointsLast365Days,
            updatedAt: new Date()
          });
        }

        setCustomer({
          id: customerId,
          ...customerData,
          loyaltyTier: calculatedTier, // Use the calculated tier
          pointsLast365Days: pointsLast365Days,
          createdAt: customerData.createdAt?.seconds ? new Date(customerData.createdAt.seconds * 1000) : customerData.createdAt,
          lastLoginAt: customerData.lastLoginAt?.seconds ? new Date(customerData.lastLoginAt.seconds * 1000) : customerData.lastLoginAt
        });
      } else {
        throw new Error('Customer not found');
      }
    } catch (err) {
      console.error('Error loading customer:', err);
      setError('Failed to load customer: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async (tab) => {
    try {
      setTabLoading(true);
      setError('');
      console.log(`Starting to load ${tab} data...`);

      switch (tab) {
        case 'appointments':
          await loadAppointments();
          await loadServices();
          await loadStaff();
          break;
        case 'vouchers':
          loadVouchers();
          break;
        case 'transactions':
          await loadTransactions();
          await loadServices();
          await loadStaff();
          await loadProducts();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Error loading ${tab} data:`, err);
      setError(`Failed to load ${tab} data: ${err.message}`);
    } finally {
      setTabLoading(false);
      console.log(`Finished loading ${tab} data`);
    }
  };

  const loadAppointments = async () => {
    const toDate = (val) => {
      if (!val) return null;
      if (val.toDate) return val.toDate();
      if (val.seconds) return new Date(val.seconds * 1000);
      if (val instanceof Date) return val;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    };

    try {
      console.log('Loading appointments for customer:', customerId);

      const customerEmail = customer?.email || customerId.split('_')[0];
      console.log('Extracted customer email:', customerEmail);
      console.log('Using salon ID:', salonId);
      
      // Query main appointments collection
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsQuery = query(
        appointmentsRef, 
        where('customerEmail', '==', customerEmail),
        where('salonId', '==', salonId),
        orderBy('dateTimeCreated', 'desc')
      );
      
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      
      const appointmentsData = [];
      appointmentsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('Raw appointment data:', data);

        const appointmentDate = toDate(data.appointmentDate);
        const startDateTime   = toDate(data.startDateTime);
        const createdAt       = toDate(data.createdAt);

        appointmentsData.push({
          id: doc.id,
          ...data,
          dateTimeRequested: toDate(data.dateTimeRequested) || toDate(data.requestedDate) || appointmentDate || startDateTime,
          dateTimeCreated:   toDate(data.dateTimeCreated)   || createdAt       || startDateTime,
          appointmentDate,
          updatedAt: toDate(data.updatedAt)
        });
      });

      console.log(`Setting appointments state with ${appointmentsData.length} items:`, appointmentsData);
      setAppointments(appointmentsData);
      
    } catch (err) {
      console.error('Error loading appointments:', err);
      // If orderBy fails due to missing index, try without ordering
      try {
        console.log('Retrying appointments query without orderBy...');
        const appointmentsRef = collection(db, 'appointments');
        const appointmentsQuery = query(
          appointmentsRef, 
          where('customerEmail', '==', customer?.email || customerId.split('_')[0]),
          where('salonId', '==', salonId)
        );

        const appointmentsSnapshot = await getDocs(appointmentsQuery);
        
        const appointmentsData = [];
        appointmentsSnapshot.forEach(doc => {
          const data = doc.data();
          const appointmentDate = toDate(data.appointmentDate);
          const startDateTime   = toDate(data.startDateTime);
          const createdAt       = toDate(data.createdAt);
          appointmentsData.push({
            id: doc.id,
            ...data,
            dateTimeRequested: toDate(data.dateTimeRequested) || toDate(data.requestedDate) || appointmentDate || startDateTime,
            dateTimeCreated:   toDate(data.dateTimeCreated)   || createdAt       || startDateTime,
            appointmentDate,
            updatedAt: toDate(data.updatedAt)
          });
        });
        
        // Sort manually by creation date (newest first)
        appointmentsData.sort((a, b) => {
          const dateA = a.dateTimeCreated || a.appointmentDate || new Date(0);
          const dateB = b.dateTimeCreated || b.appointmentDate || new Date(0);
          return dateB - dateA;
        });
        
        console.log(`Setting appointments state with ${appointmentsData.length} items (fallback):`, appointmentsData);
        setAppointments(appointmentsData);
        
      } catch (fallbackErr) {
        console.error('Error in fallback appointments query:', fallbackErr);
        setAppointments([]);
      }
    }
  };

  const parseVoucherTimestamp = (val) => {
    if (!val) return val;
    if (val.toDate) return val.toDate();
    if (val.seconds) return new Date(val.seconds * 1000);
    return val;
  };

  const processVoucherSnapshot = (snapshot) => {
    const vouchersData = [];
    snapshot.forEach(d => {
      const data = d.data();
      vouchersData.push({
        id: d.id,
        ...data,
        createdAt: parseVoucherTimestamp(data.createdAt),
        startDate: parseVoucherTimestamp(data.startDate),
        endDate: parseVoucherTimestamp(data.endDate),
        redeemedAt: parseVoucherTimestamp(data.redeemedAt)
      });
    });
    return vouchersData;
  };

  const loadVouchersFallback = (customerEmail) => {
    const vouchersRef = collection(db, 'vouchers');
    const vouchersQuery = query(
      vouchersRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );

    const unsubscribe = onSnapshot(
      vouchersQuery,
      (snapshot) => {
        const vouchersData = processVoucherSnapshot(snapshot);
        vouchersData.sort((a, b) => {
          const dateA = a.createdAt || new Date(0);
          const dateB = b.createdAt || new Date(0);
          return dateB - dateA;
        });
        setVouchers(vouchersData);
      },
      (error) => {
        console.error('Error in fallback vouchers listener:', error);
        setVouchers([]);
      }
    );

    vouchersUnsubscribeRef.current = unsubscribe;
  };

  const loadVouchers = () => {
    // Clean up any previous listener
    if (vouchersUnsubscribeRef.current) {
      vouchersUnsubscribeRef.current();
      vouchersUnsubscribeRef.current = null;
    }

    const customerEmail = customer?.email || customerId.split('_')[0];

    const vouchersRef = collection(db, 'vouchers');
    const vouchersQuery = query(
      vouchersRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      vouchersQuery,
      (snapshot) => {
        const vouchersData = processVoucherSnapshot(snapshot);
        setVouchers(vouchersData);
      },
      (error) => {
        console.error('Error in vouchers listener, using fallback:', error);
        loadVouchersFallback(customerEmail);
      }
    );

    vouchersUnsubscribeRef.current = unsubscribe;
  };

  const loadTransactions = async () => {
    try {
      console.log('Loading transactions for customer:', customerId);

      const customerEmail = customer?.email || customerId.split('_')[0];
      console.log('Extracted customer email:', customerEmail);
      console.log('Using salon ID:', salonId);
      
      // Query main transactions collection
      const transactionsRef = collection(db, 'transactions');
      const transactionsQuery = query(
        transactionsRef, 
        where('customerEmail', '==', customerEmail),
        where('salonId', '==', salonId),
        orderBy('createdAt', 'desc')
      );
      
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      const transactionsData = [];
      transactionsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('Raw transaction data:', data);
        
        transactionsData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : 
                    data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : 
                    data.createdAt,
          paidAt: data.paidAt?.toDate ? data.paidAt.toDate() : 
                 data.paidAt?.seconds ? new Date(data.paidAt.seconds * 1000) : 
                 data.paidAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : 
                    data.updatedAt?.seconds ? new Date(data.updatedAt.seconds * 1000) : 
                    data.updatedAt
        });
      });
      
      // Wallet top-up requests only become a real transaction once the customer
      // has uploaded their payment proof — a pending request with no screenshot
      // yet is an abandoned draft and should not appear here.
      const visibleTransactionsData = transactionsData.filter(t => !(t.type === 'wallet_topup' && !t.proofImageUrl));

      console.log(`Setting transactions state with ${visibleTransactionsData.length} items:`, visibleTransactionsData);
      setTransactions(visibleTransactionsData);

    } catch (err) {
      console.error('Error loading transactions:', err);
      // If orderBy fails due to missing index, try without ordering
      try {
        console.log('Retrying transactions query without orderBy...');
        const transactionsRef = collection(db, 'transactions');
        const transactionsQuery = query(
          transactionsRef, 
          where('customerEmail', '==', customer?.email || customerId.split('_')[0]),
          where('salonId', '==', salonId)
        );

        const transactionsSnapshot = await getDocs(transactionsQuery);
        
        const transactionsData = [];
        transactionsSnapshot.forEach(doc => {
          const data = doc.data();
          transactionsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : 
                      data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : 
                      data.createdAt,
            paidAt: data.paidAt?.toDate ? data.paidAt.toDate() : 
                   data.paidAt?.seconds ? new Date(data.paidAt.seconds * 1000) : 
                   data.paidAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : 
                      data.updatedAt?.seconds ? new Date(data.updatedAt.seconds * 1000) : 
                      data.updatedAt
          });
        });
        
        // Sort manually by creation date (newest first)
        transactionsData.sort((a, b) => {
          const dateA = a.createdAt || new Date(0);
          const dateB = b.createdAt || new Date(0);
          return dateB - dateA;
        });
        
        const visibleTransactionsData = transactionsData.filter(t => !(t.type === 'wallet_topup' && !t.proofImageUrl));

        console.log(`Setting transactions state with ${visibleTransactionsData.length} items (fallback):`, visibleTransactionsData);
        setTransactions(visibleTransactionsData);

      } catch (fallbackErr) {
        console.error('Error in fallback transactions query:', fallbackErr);
        setTransactions([]);
      }
    }
  };

  const loadServices = async () => {
    try {
      console.log('Loading services for salon:', salonId);
      
      const servicesRef = collection(db, 'services');
      const servicesQuery = query(servicesRef, where('salonId', '==', salonId));
      const servicesSnapshot = await getDocs(servicesQuery);
      
      const servicesData = [];
      servicesSnapshot.forEach(doc => {
        servicesData.push({ id: doc.id, ...doc.data() });
      });
      
      setServices(servicesData);
      console.log(`Found ${servicesData.length} services for salon ${salonId}`);
      
    } catch (err) {
      console.error('Error loading services:', err);
      setServices([]);
    }
  };

  const loadStaff = async () => {
    try {
      console.log('Loading staff for salon:', salonId);

      const staffRef = collection(db, 'staff');
      const staffQuery = query(staffRef, where('salonId', '==', salonId));
      const staffSnapshot = await getDocs(staffQuery);

      const staffData = [];
      staffSnapshot.forEach(doc => {
        staffData.push({ id: doc.id, ...doc.data() });
      });

      setStaff(staffData);
      console.log(`Found ${staffData.length} staff for salon ${salonId}`);

    } catch (err) {
      console.error('Error loading staff:', err);
      setStaff([]);
    }
  };

  const loadProducts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const productsQuery = query(productsRef, where('salonId', '==', salonId));
      const productsSnapshot = await getDocs(productsQuery);
      const productsData = [];
      productsSnapshot.forEach(d => productsData.push({ id: d.id, ...d.data() }));
      setProducts(productsData);
      console.log(`Found ${productsData.length} products for salon ${salonId}`);
    } catch (err) {
      console.error('Error loading products:', err);
      setProducts([]);
    }
  };

  // Return active products available at the given locationId (empty locationIds = all locations)
  const getProductsForLocation = (locationId) => {
    const active = products.filter(p => p.isActive !== false);
    if (!locationId) return active;
    return active.filter(p =>
      !p.locationIds || p.locationIds.length === 0 || p.locationIds.includes(locationId)
    );
  };


  // Send loyalty points earned email (non-blocking). Template comes from the salon document (Firestore);
  // {total_points} is read live after the balance increment so it is never stale.
  const sendLoyaltyPointsEmail = (pointsEarned, computedBalance) => {
    // WhatsApp points notice (backend applies the salon toggle, opt-in and reads the live balance). Independent of the
    // e-mail below, so it also reaches customers who have no e-mail address.
    if (pointsEarned > 0) {
      whatsappService.sendTransactional({ templateId: 'loyalty_points_earned', customerId, pointsEarned }, salonId);
    }
    (async () => {
      try {
        if (!customer?.email || pointsEarned <= 0) return;
        const cfg = await loadSalonNotificationConfig(salonId);
        if (cfg.settings['email_loyalty_points_earned'] === false) return;

        const totalPoints = await fetchPointsBalance(customerId, computedBalance);
        const vars = {
          name: customer.displayName || customer.email.split('@')[0],
          points_earned: pointsEarned.toLocaleString(),
          total_points: totalPoints.toLocaleString()
        };
        const template = resolveEmailTemplate(cfg, 'loyalty_points_earned');

        await addDoc(collection(db, 'mail'), {
          to: customer.email,
          salonId: salonId,
          message: {
            subject: renderTemplate(template.subject, vars),
            html: renderTemplate(template.html, vars),
            text: renderTemplate(template.body, vars)
          },
          metadata: {
            campaignType: 'loyalty_notification',
            salonId: salonId,
            customerId: customerId
          }
        });

        console.log(`Loyalty points email queued for ${customer.email} (${pointsEarned} points earned)`);
      } catch (err) {
        console.error('Loyalty points email failed (non-blocking):', err);
      }
    })();
  };

  // Send "Payment Received" push notification (non-blocking) - wording from the salon's push template
  const sendPaymentPushNotification = async (amount) => {
    if (!customer?.email) return;
    const cfg = await loadSalonNotificationConfig(salonId);
    if (cfg.settings['push_payment_confirmation'] === false) return;
    const vars = { name: customer.displayName || customer.email.split('@')[0], amount: formatMoney(amount) };
    const template = resolvePushTemplate(cfg, 'payment_confirmation');
    sendTransactionalPushNotification({
      customerEmail: customer.email,
      salonId,
      title: renderTemplate(template.title, vars),
      body: renderTemplate(template.body, vars),
      data: { type: 'payment_received' }
    }).catch(err => console.error('Payment push notification failed (non-blocking):', err));
  };

  // Send "Points Earned" push notification (non-blocking) - {total_points} is read live after the increment
  const sendPointsEarnedPushNotification = async (pointsEarned, computedBalance) => {
    if (!customer?.email || pointsEarned <= 0) return;
    const cfg = await loadSalonNotificationConfig(salonId);
    if (cfg.settings['push_loyalty_update'] === false) return;
    const totalPoints = await fetchPointsBalance(customerId, computedBalance);
    const vars = {
      name: customer.displayName || customer.email.split('@')[0],
      points_earned: pointsEarned.toLocaleString(),
      total_points: totalPoints.toLocaleString()
    };
    const template = resolvePushTemplate(cfg, 'loyalty_update');
    sendTransactionalPushNotification({
      customerEmail: customer.email,
      salonId,
      title: renderTemplate(template.title, vars),
      body: renderTemplate(template.body, vars),
      data: { type: 'points_earned' }
    }).catch(err => console.error('Points push notification failed (non-blocking):', err));
  };

  // Send "Wallet Debited" push notification (non-blocking) — fired after a Wallet-payment-method transaction
  // deducts the balance. The new balance is read live; template + toggle come from the salon (push editor).
  const sendWalletDebitPushNotification = async (amount, computedBalance) => {
    if (!customer?.email) return;
    const newBalance = await fetchWalletBalance(customerId, computedBalance);
    notifyWalletDebit({
      salonId,
      customerEmail: customer.email,
      customerName: customer.displayName,
      amount,
      newBalance
    });
  };

  // Send "Wallet Top-Up" push notification (non-blocking) — fired after an
  // admin verifies or rejects a customer's top-up proof.
  const sendWalletTopupPushNotification = async (topupTransaction, verified, newBalance) => {
    if (!topupTransaction?.customerEmail) return;
    notifyWalletTopup({
      salonId,
      customerEmail: topupTransaction.customerEmail,
      customerName: customer?.displayName,
      amount: topupTransaction.amount,
      verified,
      newBalance
    });
  };

  // Admin verifies a customer's wallet top-up proof — credits their balance
  // via the trusted backend route (never a client-side updateDoc for this).
  const handleVerifyTopup = async (transaction) => {
    setWalletActionLoadingId(transaction.id);
    try {
      const result = await verifyTopup(transaction.id, salonId);
      await loadTransactions();
      sendWalletTopupPushNotification(transaction, true, result.newBalance);
    } catch (err) {
      console.error('Error verifying wallet top-up:', err);
      setError(err.message || 'Failed to verify top-up');
    } finally {
      setWalletActionLoadingId(null);
    }
  };

  // Admin rejects a customer's wallet top-up proof — balance is untouched.
  const handleRejectTopup = async (transaction) => {
    setWalletActionLoadingId(transaction.id);
    try {
      await rejectTopup(transaction.id, salonId);
      await loadTransactions();
      sendWalletTopupPushNotification(transaction, false, null);
    } catch (err) {
      console.error('Error rejecting wallet top-up:', err);
      setError(err.message || 'Failed to reject top-up');
    } finally {
      setWalletActionLoadingId(null);
    }
  };

  // Send payment receipt email (non-blocking). Template comes from the salon document (Firestore).
  const sendPaymentReceiptEmail = (amount, transactionId) => {
    // WhatsApp receipt (backend applies the salon toggle + opt-in; amount/invoice come from the transaction itself)
    if (transactionId) {
      whatsappService.sendTransactional({ templateId: 'payment_received', transactionId }, salonId);
    }
    (async () => {
      try {
        if (!customer?.email || !transactionId) return;
        const cfg = await loadSalonNotificationConfig(salonId);
        if (cfg.settings['email_payment_receipt'] === false) return;

        const vars = {
          name: customer.displayName || customer.email.split('@')[0],
          amount: formatMoney(amount),
          invoice_number: makeInvoiceNumber(transactionId)
        };
        const template = resolveEmailTemplate(cfg, 'payment_receipt');

        await addDoc(collection(db, 'mail'), {
          to: customer.email,
          salonId: salonId,
          message: {
            subject: renderTemplate(template.subject, vars),
            html: renderTemplate(template.html, vars),
            text: renderTemplate(template.body, vars)
          },
          metadata: {
            campaignType: 'payment_receipt',
            salonId: salonId,
            customerId: customerId
          }
        });

        console.log(`Payment receipt email queued for ${customer.email} (Invoice: ${vars.invoice_number})`);
      } catch (err) {
        console.error('Payment receipt email failed (non-blocking):', err);
      }
    })();
  };

  // Close the Add Transaction modal and clear any voucher state
  const handleCloseAddTransaction = () => {
    setShowAddTransaction(false);
    setActiveVoucher(null);
    setVoucherApplied(false);
    setNettPrice(null);
    setTransactionForm({
      serviceId: '',
      staffId: '',
      customPrice: '',
      paymentMethod: 'cash',
      paymentStatus: 'Pending',
      locationId: ''
    });
  };

  // Apply the active voucher discount to the entered Custom Price.
  // Works for both the service modal (transactionForm) and the product modal
  // (productTransactionForm) — detects which is open via showAddProductTransaction.
  const handleApplyVoucher = () => {
    const rawPrice = showAddProductTransaction
      ? productTransactionForm.customPrice
      : transactionForm.customPrice;
    const customPriceNum = parseFloat(rawPrice);
    if (!customPriceNum || isNaN(customPriceNum) || customPriceNum <= 0) {
      setError('Please enter a Custom Price before applying the voucher discount.');
      return;
    }

    let calculated = customPriceNum;
    const v = activeVoucher;

    if (v.type === 'percentage') {
      calculated = customPriceNum * (1 - (v.discountValue || 0) / 100);
    } else if (v.type === 'fixed_amount') {
      calculated = Math.max(0, customPriceNum - (v.discountValue || 0));
    } else if (v.type === 'points_based') {
      calculated = Math.max(0, customPriceNum - (v.voucherAmount || 0));
    }
    // free_gift: no price change, just mark the voucher used

    setNettPrice(parseFloat(calculated.toFixed(2)));
    setVoucherApplied(true);
    setError('');
  };

  const handleAddTransaction = async () => {
    // Synchronous guard prevents duplicate notifications from rapid double-click
    if (isAddingTransactionRef.current) return;
    isAddingTransactionRef.current = true;

    try {
      if (!transactionForm.serviceId) {
        isAddingTransactionRef.current = false;
        setError('Please select a service');
        return;
      }

      if (!transactionForm.staffId) {
        isAddingTransactionRef.current = false;
        setError('Please select a staff member');
        return;
      }

      if (transactionForm.customPrice === '' || transactionForm.customPrice === null || transactionForm.customPrice === undefined) {
        isAddingTransactionRef.current = false;
        setError('Please enter a price for this service');
        return;
      }

      setTabLoading(true);
      setError('');

      const selectedService = services.find(s => s.id === transactionForm.serviceId);
      const selectedStaff = staff.find(s => s.id === transactionForm.staffId);
      
      if (!selectedService) {
        throw new Error('Selected service not found');
      }

      if (!selectedStaff) {
        throw new Error('Selected staff member not found');
      }

      // Determine the price to charge:
      // If a voucher discount has been calculated, use nettPrice; otherwise use customPrice.
      const customPriceNum = parseFloat(transactionForm.customPrice);
      const finalPrice = (activeVoucher && voucherApplied && nettPrice !== null)
        ? nettPrice
        : customPriceNum;

      // Points payment validation (for 'Points' payment method — unrelated to voucher)
      let pointsUsedForTransaction = 0;
      if (transactionForm.paymentMethod === 'Points') {
        const pointValue = loyaltyConfig?.pointValue || 0.1;
        pointsUsedForTransaction = Math.ceil(finalPrice / pointValue);
        const pointsBalance = customer?.metrics?.pointsBalance || 0;

        if (pointsBalance < pointsUsedForTransaction) {
          setError(`Insufficient points. Required: ${pointsUsedForTransaction.toLocaleString()}, Available: ${pointsBalance.toLocaleString()}`);
          setTabLoading(false);
          return;
        }
      }

      // Wallet payment validation
      let walletAmountUsed = 0;
      if (transactionForm.paymentMethod === 'Wallet') {
        walletAmountUsed = finalPrice;
        const walletBalance = customer?.metrics?.walletBalance || 0;
        if (walletBalance < walletAmountUsed) {
          setError(`Insufficient wallet balance. Required: ${formatCurrency(walletAmountUsed)}, Available: ${formatCurrency(walletBalance)}`);
          setTabLoading(false);
          return;
        }
      }

      // Calculate points earned on the nett price (post-discount amount actually paid)
      const pointsPerCurrency = loyaltyConfig?.pointsPerCurrency || 10;
      const currencyPerPoint  = loyaltyConfig?.currencyPerPoint  || 10;
      const isCompleted = transactionForm.paymentStatus === 'Completed';
      const earnedPoints = (isCompleted && transactionForm.paymentMethod !== 'Points')
        ? Math.floor((finalPrice / currencyPerPoint) * pointsPerCurrency)
        : 0;

      // Build voucher-related fields to store on the transaction record
      const voucherFields = (activeVoucher && voucherApplied) ? {
        voucherId:      activeVoucher.id,
        voucherCode:    activeVoucher.voucherCode,
        discountType:   activeVoucher.type,
        discountAmount: parseFloat((customPriceNum - finalPrice).toFixed(2))
      } : {};

      const TAX_RATE = 0.09;
      const taxAmount   = parseFloat((finalPrice * TAX_RATE).toFixed(2));
      const totalAmount = parseFloat((finalPrice + taxAmount).toFixed(2));
      const customerName = customer.displayName || customer.name || customer.email?.split('@')[0] || 'Unknown';

      const transactionData = {
        // Identity
        type: 'service',
        salonId: salonId,
        clientId: `${salonId}-client`,
        currency: 'SGD',
        source: 'admin',
        // Customer
        customerId: customerId,
        customerEmail: customer.email,
        customerName,
        // Service & staff
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        staffId: selectedStaff.id,
        staffName: selectedStaff.name,
        duration: selectedService.duration || '60',
        // Pricing
        amount: finalPrice.toString(),          // nett price as string (alias of price)
        price: finalPrice.toString(),           // nett price (what customer pays)
        originalPrice: customPriceNum,          // admin-entered price before discount
        taxAmount,
        totalAmount,
        // Location
        locationId: transactionForm.locationId || '',
        locationName: locations.find(l => l.locationId === transactionForm.locationId)?.locationName || '',
        // Payment
        paymentMethod: transactionForm.paymentMethod,
        paymentStatus: transactionForm.paymentStatus,
        pointsUsed: pointsUsedForTransaction,
        pointsEarned: earnedPoints,
        description: '',
        appointmentId: null,
        status: isCompleted ? 'paid' : 'pending',
        // Timestamps
        paidAt: isCompleted ? new Date() : null,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...voucherFields
      };

      // Add to main transactions collection
      const transactionsRef = collection(db, 'transactions');
      const transactionDocRef = await addDoc(transactionsRef, transactionData);

      // Send payment receipt email and push notification when payment is completed (non-blocking)
      if (isCompleted) {
        sendPaymentReceiptEmail(finalPrice, transactionDocRef.id);
        sendPaymentPushNotification(finalPrice);
      }

      // Deplete linked product stock when service transaction is completed
      if (isCompleted) {
        // Support both new array (linkedProducts) and legacy flat fields
        const linkedEntries = Array.isArray(selectedService.linkedProducts) && selectedService.linkedProducts.length > 0
          ? selectedService.linkedProducts
          : (selectedService.linkedProductId && selectedService.linkedProductQty
              ? [{ productId: selectedService.linkedProductId, qty: selectedService.linkedProductQty }]
              : []);
        for (const entry of linkedEntries) {
          if (!entry.productId || !entry.qty) continue;
          try {
            const productRef = doc(db, 'products', entry.productId);
            const productSnap = await getDoc(productRef);
            if (productSnap.exists()) {
              const currentStock = productSnap.data().stockLevel;
              if (currentStock !== null && currentStock !== undefined) {
                const newStock = Math.max(0, Number(currentStock) - Number(entry.qty));
                await updateDoc(productRef, {
                  stockLevel: parseFloat(newStock.toFixed(1)),
                  stockUpdatedAt: new Date(),
                  updatedAt: new Date()
                });
              }
            }
          } catch (stockErr) {
            console.error('Failed to deplete linked product stock (non-fatal):', stockErr);
          }
        }
      }

      // Redeem the voucher and increment the promotion's counter (only on successful save)
      if (activeVoucher && voucherApplied) {
        try {
          await redeemVoucher(
            activeVoucher.id,
            transactionDocRef.id,
            activeVoucher.promotionId
          );
        } catch (redeemErr) {
          // Non-fatal: log but don't block the transaction success
          console.error('Voucher redemption update failed (transaction still saved):', redeemErr);
        }
      }

      // Deduct points for points_based voucher redemption (separate from Points payment method)
      if (activeVoucher && voucherApplied && activeVoucher.type === 'points_based' && isCompleted) {
        const pointsToDeduct = activeVoucher.pointsRequired || 0;
        if (pointsToDeduct > 0) {
          const customerRef = doc(db, 'customers', customerId);
          await updateDoc(customerRef, {
            'metrics.pointsBalance': increment(-pointsToDeduct),
            updatedAt: new Date()
          });
          console.log(`Deducted ${pointsToDeduct} points for points_based voucher redemption`);
        }
      }

      // Deduct points from customer balance if Points payment
      if (transactionForm.paymentMethod === 'Points' && pointsUsedForTransaction > 0) {
        const customerRef = doc(db, 'customers', customerId);
        await updateDoc(customerRef, {
          'metrics.pointsBalance': increment(-pointsUsedForTransaction),
          updatedAt: new Date()
        });
        console.log(`Deducted ${pointsUsedForTransaction} points from customer balance`);
      }

      // Deduct wallet balance if Wallet payment
      if (transactionForm.paymentMethod === 'Wallet' && walletAmountUsed > 0) {
        const customerRef = doc(db, 'customers', customerId);
        await updateDoc(customerRef, {
          'metrics.walletBalance': increment(-walletAmountUsed),
          updatedAt: new Date()
        });
        const currentWalletBalance = customer?.metrics?.walletBalance || 0;
        sendWalletDebitPushNotification(walletAmountUsed, currentWalletBalance - walletAmountUsed);
      }

      // Award earned points to customer balance if payment is completed
      if (earnedPoints > 0) {
        const customerRef = doc(db, 'customers', customerId);
        await updateDoc(customerRef, {
          'metrics.pointsEarned': increment(earnedPoints),
          'metrics.pointsBalance': increment(earnedPoints),
          updatedAt: new Date()
        });
        console.log(`Awarded ${earnedPoints} points to customer balance (${pointsPerCurrency} per $${currencyPerPoint})`);

        // Send loyalty points earned email and push notification (non-blocking)
        const currentBalance = customer?.metrics?.pointsBalance || 0;
        sendLoyaltyPointsEmail(earnedPoints, currentBalance + earnedPoints);
        sendPointsEarnedPushNotification(earnedPoints, currentBalance + earnedPoints);
      }

      // Calculate totalVisits based on unique transaction dates
      const allTransactionsQuery = query(
        collection(db, 'transactions'),
        where('customerEmail', '==', customer.email),
        where('salonId', '==', salonId)
      );
      const allTransactionsSnapshot = await getDocs(allTransactionsQuery);

      // Get unique dates (YYYY-MM-DD format) using local timezone
      const uniqueDates = new Set();
      allTransactionsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.createdAt) {
          let date;
          if (data.createdAt.toDate) {
            date = data.createdAt.toDate();
          } else if (data.createdAt.seconds) {
            date = new Date(data.createdAt.seconds * 1000);
          } else {
            date = new Date(data.createdAt);
          }
          // Use local date to avoid UTC timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateOnly = `${year}-${month}-${day}`;
          uniqueDates.add(dateOnly);
        }
      });
      const totalVisits = uniqueDates.size;

      // Update customer's lastVisit and totalVisits
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        lastVisit: transactionData.createdAt,
        'metrics.totalVisits': totalVisits,
        updatedAt: new Date()
      });
      console.log('Customer lastVisit updated to:', transactionData.createdAt);
      console.log('Customer totalVisits updated to:', totalVisits);

      // Recalculate totalSpent (sum of all transaction totalAmounts)
      await updateCustomerTotalSpent(customerId, customer.email, salonId);
      console.log('Customer totalSpent recalculated');

      // Recalculate totalSpentLast365Days (sum of totalAmounts in last 365 days)
      await updateCustomerTotalSpentLast365Days(customerId, customer.email, salonId);
      console.log('Customer totalSpentLast365Days recalculated');

      // Recalculate loyalty tier based on points in last 365 days
      await updateCustomerLoyaltyTier(customerId, customer.email, salonId);
      console.log('Customer loyalty tier recalculated');

      setSuccessMessage('Transaction added successfully!');
      setShowAddTransaction(false);
      setActiveVoucher(null);
      setVoucherApplied(false);
      setNettPrice(null);

      setTransactionForm({
        serviceId: '',
        staffId: '',
        customPrice: '',
        paymentMethod: 'cash',
        paymentStatus: 'Pending',
        locationId: ''
      });

      await loadTransactions();
      await loadCustomerData();
      
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error adding transaction:', err);
      setError('Failed to add transaction. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
      isAddingTransactionRef.current = false;
    }
  };

  // ── Product transaction helpers ──────────────────────────────────────────

  const handleCloseProductTransaction = () => {
    setShowAddProductTransaction(false);
    setActiveVoucher(null);
    setVoucherApplied(false);
    setNettPrice(null);
    setProductTransactionForm({
      productId: '',
      quantity: 1,
      staffId: '',
      customPrice: '',
      paymentMethod: 'cash',
      paymentStatus: 'Pending',
      locationId: ''
    });
  };

  const handleClosePackageTransaction = () => {
    setShowAddPackageTransaction(false);
    setPackageTransactionForm({
      packageName: '',
      quantity: 1,
      staffId: '',
      customPrice: '',
      paymentMethod: 'cash',
      paymentStatus: 'Pending',
      locationId: ''
    });
  };

  const handleAddPackageTransaction = async () => {
    if (isAddingPackageTransactionRef.current) return;
    isAddingPackageTransactionRef.current = true;

    try {
      if (!packageTransactionForm.packageName) {
        isAddingPackageTransactionRef.current = false;
        setError('Please select a package');
        return;
      }
      if (!packageTransactionForm.staffId) {
        isAddingPackageTransactionRef.current = false;
        setError('Please select a staff member');
        return;
      }
      if (packageTransactionForm.customPrice === '' || packageTransactionForm.customPrice === null) {
        isAddingPackageTransactionRef.current = false;
        setError('Please enter a price for this package');
        return;
      }

      setTabLoading(true);
      setError('');

      const selectedStaff = staff.find(s => s.id === packageTransactionForm.staffId);
      if (!selectedStaff) throw new Error('Selected staff member not found');

      const qty = Math.max(1, parseInt(packageTransactionForm.quantity) || 1);
      const finalPrice = parseFloat(packageTransactionForm.customPrice);
      const isCompleted = packageTransactionForm.paymentStatus === 'Completed';

      const pointsPerCurrency = loyaltyConfig?.pointsPerCurrency || 10;
      const currencyPerPoint  = loyaltyConfig?.currencyPerPoint  || 10;
      const earnedPoints = (isCompleted && packageTransactionForm.paymentMethod !== 'Points')
        ? Math.floor((finalPrice / currencyPerPoint) * pointsPerCurrency)
        : 0;

      // Points payment handling
      let pointsUsedForTransaction = 0;
      if (packageTransactionForm.paymentMethod === 'Points') {
        const pointValue = loyaltyConfig?.pointValue || 0.1;
        pointsUsedForTransaction = Math.ceil(finalPrice / pointValue);
        const pointsBalance = customer?.metrics?.pointsBalance || 0;
        if (pointsBalance < pointsUsedForTransaction) {
          setError(`Insufficient points. Required: ${pointsUsedForTransaction.toLocaleString()}, Available: ${pointsBalance.toLocaleString()}`);
          setTabLoading(false);
          isAddingPackageTransactionRef.current = false;
          return;
        }
      }

      // Wallet payment handling
      let walletAmountUsed = 0;
      if (packageTransactionForm.paymentMethod === 'Wallet') {
        walletAmountUsed = finalPrice;
        const walletBalance = customer?.metrics?.walletBalance || 0;
        if (walletBalance < walletAmountUsed) {
          setError(`Insufficient wallet balance. Required: ${formatCurrency(walletAmountUsed)}, Available: ${formatCurrency(walletBalance)}`);
          setTabLoading(false);
          isAddingPackageTransactionRef.current = false;
          return;
        }
      }

      const TAX_RATE    = 0.09;
      const taxAmount   = parseFloat((finalPrice * TAX_RATE).toFixed(2));
      const totalAmount = parseFloat((finalPrice + taxAmount).toFixed(2));
      const customerName = customer.displayName || customer.name || customer.email?.split('@')[0] || 'Unknown';

      const transactionData = {
        type: 'package',
        salonId,
        clientId: `${salonId}-client`,
        currency: 'SGD',
        source: 'admin',
        customerId,
        customerEmail: customer.email,
        customerName,
        packageName:  packageTransactionForm.packageName,
        staffId:      selectedStaff.id,
        staffName:    selectedStaff.name,
        quantity:     qty,
        amount:       finalPrice.toString(),
        price:        finalPrice.toString(),
        originalPrice: finalPrice,
        taxAmount,
        totalAmount,
        locationId:   packageTransactionForm.locationId || '',
        locationName: locations.find(l => l.locationId === packageTransactionForm.locationId)?.locationName || '',
        paymentMethod: packageTransactionForm.paymentMethod,
        paymentStatus: packageTransactionForm.paymentStatus,
        pointsUsed:    pointsUsedForTransaction,
        pointsEarned:  earnedPoints,
        description:   '',
        appointmentId: null,
        status:        isCompleted ? 'paid' : 'pending',
        paidAt:        isCompleted ? new Date() : null,
        createdAt:     new Date(),
        updatedAt:     new Date()
      };

      const transactionDocRef = await addDoc(collection(db, 'transactions'), transactionData);

      if (isCompleted) {
        sendPaymentReceiptEmail(finalPrice, transactionDocRef.id);
        sendPaymentPushNotification(finalPrice);
      }

      if (packageTransactionForm.paymentMethod === 'Points' && pointsUsedForTransaction > 0) {
        await updateDoc(doc(db, 'customers', customerId), {
          'metrics.pointsBalance': increment(-pointsUsedForTransaction),
          updatedAt: new Date()
        });
      }

      if (packageTransactionForm.paymentMethod === 'Wallet' && walletAmountUsed > 0) {
        await updateDoc(doc(db, 'customers', customerId), {
          'metrics.walletBalance': increment(-walletAmountUsed),
          updatedAt: new Date()
        });
        const currentWalletBalance = customer?.metrics?.walletBalance || 0;
        sendWalletDebitPushNotification(walletAmountUsed, currentWalletBalance - walletAmountUsed);
      }

      if (earnedPoints > 0) {
        const currentBalance = customer?.metrics?.pointsBalance || 0;
        await updateDoc(doc(db, 'customers', customerId), {
          'metrics.pointsEarned':  increment(earnedPoints),
          'metrics.pointsBalance': increment(earnedPoints),
          updatedAt: new Date()
        });
        sendLoyaltyPointsEmail(earnedPoints, currentBalance + earnedPoints);
        sendPointsEarnedPushNotification(earnedPoints, currentBalance + earnedPoints);
      }

      await updateDoc(doc(db, 'customers', customerId), {
        lastVisit: transactionData.createdAt,
        updatedAt: new Date()
      });

      await updateCustomerTotalSpent(customerId, customer.email, salonId);
      await updateCustomerTotalSpentLast365Days(customerId, customer.email, salonId);
      await updateCustomerLoyaltyTier(customerId, customer.email, salonId);

      setSuccessMessage('Package transaction added successfully!');
      handleClosePackageTransaction();
      await loadTransactions();
      await loadCustomerData();
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('Error adding package transaction:', err);
      setError('Failed to add package transaction. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
      isAddingPackageTransactionRef.current = false;
    }
  };

  const getProductPointsRequired = () => {
    const finalPrice = parseFloat(productTransactionForm.customPrice) || 0;
    if (finalPrice <= 0) return 0;
    const pointValue = loyaltyConfig?.pointValue || 0.1;
    return Math.ceil(finalPrice / pointValue);
  };

  const canRedeemProductWithPoints = () => {
    const required = getProductPointsRequired();
    if (required <= 0) return false;
    return (customer?.metrics?.pointsBalance || 0) >= required;
  };

  const canPayProductWithWallet = () => {
    const finalPrice = parseFloat(productTransactionForm.customPrice) || 0;
    if (finalPrice <= 0) return false;
    return (customer?.metrics?.walletBalance || 0) >= finalPrice;
  };

  const handleAddProductTransaction = async () => {
    if (isAddingProductTransactionRef.current) return;
    isAddingProductTransactionRef.current = true;

    try {
      if (!productTransactionForm.productId) {
        isAddingProductTransactionRef.current = false;
        setError('Please select a product');
        return;
      }
      if (!productTransactionForm.staffId) {
        isAddingProductTransactionRef.current = false;
        setError('Please select a staff member');
        return;
      }
      if (productTransactionForm.customPrice === '' || productTransactionForm.customPrice === null) {
        isAddingProductTransactionRef.current = false;
        setError('Please enter a price for this product');
        return;
      }

      setTabLoading(true);
      setError('');

      const selectedProduct = products.find(p => p.id === productTransactionForm.productId);
      const selectedStaff   = staff.find(s => s.id === productTransactionForm.staffId);

      if (!selectedProduct) throw new Error('Selected product not found');
      if (!selectedStaff)   throw new Error('Selected staff member not found');

      const qty           = Math.max(1, parseInt(productTransactionForm.quantity) || 1);
      const customPriceNum = parseFloat(productTransactionForm.customPrice);
      const finalPrice = (activeVoucher && voucherApplied && nettPrice !== null)
        ? nettPrice
        : customPriceNum;

      // Points payment validation
      let pointsUsedForTransaction = 0;
      if (productTransactionForm.paymentMethod === 'Points') {
        const pointValue = loyaltyConfig?.pointValue || 0.1;
        pointsUsedForTransaction = Math.ceil(finalPrice / pointValue);
        const pointsBalance = customer?.metrics?.pointsBalance || 0;
        if (pointsBalance < pointsUsedForTransaction) {
          setError(`Insufficient points. Required: ${pointsUsedForTransaction.toLocaleString()}, Available: ${pointsBalance.toLocaleString()}`);
          setTabLoading(false);
          isAddingProductTransactionRef.current = false;
          return;
        }
      }

      // Wallet payment validation
      let walletAmountUsed = 0;
      if (productTransactionForm.paymentMethod === 'Wallet') {
        walletAmountUsed = finalPrice;
        const walletBalance = customer?.metrics?.walletBalance || 0;
        if (walletBalance < walletAmountUsed) {
          setError(`Insufficient wallet balance. Required: ${formatCurrency(walletAmountUsed)}, Available: ${formatCurrency(walletBalance)}`);
          setTabLoading(false);
          isAddingProductTransactionRef.current = false;
          return;
        }
      }

      const pointsPerCurrency = loyaltyConfig?.pointsPerCurrency || 10;
      const currencyPerPoint  = loyaltyConfig?.currencyPerPoint  || 10;
      const isCompleted = productTransactionForm.paymentStatus === 'Completed';
      const earnedPoints = (isCompleted && productTransactionForm.paymentMethod !== 'Points')
        ? Math.floor((finalPrice / currencyPerPoint) * pointsPerCurrency)
        : 0;

      const voucherFields = (activeVoucher && voucherApplied) ? {
        voucherId:      activeVoucher.id,
        voucherCode:    activeVoucher.voucherCode,
        discountType:   activeVoucher.type,
        discountAmount: parseFloat((customPriceNum - finalPrice).toFixed(2))
      } : {};

      const TAX_RATE    = 0.09;
      const taxAmount   = parseFloat((finalPrice * TAX_RATE).toFixed(2));
      const totalAmount = parseFloat((finalPrice + taxAmount).toFixed(2));
      const customerName = customer.displayName || customer.name || customer.email?.split('@')[0] || 'Unknown';

      const transactionData = {
        type: 'product',
        salonId,
        clientId: `${salonId}-client`,
        currency: 'SGD',
        source: 'admin',
        // Customer
        customerId,
        customerEmail: customer.email,
        customerName,
        // Product & staff
        productId:       selectedProduct.id,
        productName:     selectedProduct.name,
        productCategory: selectedProduct.category || '',
        staffId:         selectedStaff.id,
        staffName:       selectedStaff.name,
        quantity:        qty,
        // Pricing
        amount:        finalPrice.toString(),
        price:         finalPrice.toString(),
        originalPrice: customPriceNum,
        taxAmount,
        totalAmount,
        // Location
        locationId:   productTransactionForm.locationId || '',
        locationName: locations.find(l => l.locationId === productTransactionForm.locationId)?.locationName || '',
        // Payment
        paymentMethod:  productTransactionForm.paymentMethod,
        paymentStatus:  productTransactionForm.paymentStatus,
        pointsUsed:     pointsUsedForTransaction,
        pointsEarned:   earnedPoints,
        description:    '',
        appointmentId:  null,
        status:         isCompleted ? 'paid' : 'pending',
        paidAt:         isCompleted ? new Date() : null,
        createdAt:      new Date(),
        updatedAt:      new Date(),
        ...voucherFields
      };

      const transactionsRef  = collection(db, 'transactions');
      const transactionDocRef = await addDoc(transactionsRef, transactionData);

      // Non-blocking notifications
      if (isCompleted) {
        sendPaymentReceiptEmail(finalPrice, transactionDocRef.id);
        sendPaymentPushNotification(finalPrice);
      }

      // Redeem voucher
      if (activeVoucher && voucherApplied) {
        try {
          await redeemVoucher(activeVoucher.id, transactionDocRef.id, activeVoucher.promotionId);
        } catch (e) {
          console.error('Voucher redemption failed (non-blocking):', e);
        }
      }

      // Deduct points for points_based voucher
      if (activeVoucher && voucherApplied && activeVoucher.type === 'points_based' && isCompleted) {
        const toDeduct = activeVoucher.pointsRequired || 0;
        if (toDeduct > 0) {
          await updateDoc(doc(db, 'customers', customerId), {
            'metrics.pointsBalance': increment(-toDeduct),
            updatedAt: new Date()
          });
        }
      }

      // Deduct points for Points payment method
      if (productTransactionForm.paymentMethod === 'Points' && pointsUsedForTransaction > 0) {
        await updateDoc(doc(db, 'customers', customerId), {
          'metrics.pointsBalance': increment(-pointsUsedForTransaction),
          updatedAt: new Date()
        });
      }

      // Deduct wallet balance if Wallet payment
      if (productTransactionForm.paymentMethod === 'Wallet' && walletAmountUsed > 0) {
        await updateDoc(doc(db, 'customers', customerId), {
          'metrics.walletBalance': increment(-walletAmountUsed),
          updatedAt: new Date()
        });
        const currentWalletBalance = customer?.metrics?.walletBalance || 0;
        sendWalletDebitPushNotification(walletAmountUsed, currentWalletBalance - walletAmountUsed);
      }

      // Award earned points
      if (earnedPoints > 0) {
        const currentBalance = customer?.metrics?.pointsBalance || 0;
        await updateDoc(doc(db, 'customers', customerId), {
          'metrics.pointsEarned':  increment(earnedPoints),
          'metrics.pointsBalance': increment(earnedPoints),
          updatedAt: new Date()
        });
        sendLoyaltyPointsEmail(earnedPoints, currentBalance + earnedPoints);
        sendPointsEarnedPushNotification(earnedPoints, currentBalance + earnedPoints);
      }

      // NOTE: totalVisits is NOT incremented for product transactions (per business decision).
      // We still update lastVisit so the customer record stays fresh.
      await updateDoc(doc(db, 'customers', customerId), {
        lastVisit:  transactionData.createdAt,
        updatedAt:  new Date()
      });

      // Recalculate spend & loyalty tier
      await updateCustomerTotalSpent(customerId, customer.email, salonId);
      await updateCustomerTotalSpentLast365Days(customerId, customer.email, salonId);
      await updateCustomerLoyaltyTier(customerId, customer.email, salonId);

      // Auto-decrement stock when payment is Completed
      if (isCompleted) {
        try {
          const productRef  = doc(db, 'products', selectedProduct.id);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const currentStock = productSnap.data().stockLevel;
            if (currentStock !== null && currentStock !== undefined) {
              const newStock = Math.max(0, Number(currentStock) - qty);
              await updateDoc(productRef, {
                stockLevel:     newStock,
                stockUpdatedAt: new Date(),
                updatedAt:      new Date()
              });
            }
          }
        } catch (stockErr) {
          console.error('Stock decrement failed (non-blocking):', stockErr);
        }
      }

      setSuccessMessage('Product transaction added successfully!');
      handleCloseProductTransaction();
      await loadTransactions();
      await loadCustomerData();
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('Error adding product transaction:', err);
      setError('Failed to add product transaction. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
      isAddingProductTransactionRef.current = false;
    }
  };

  const handleGenerateProductPayNowQR = async () => {
    try {
      if (!productTransactionForm.productId) { setError('Please select a product'); return; }
      if (!productTransactionForm.staffId)   { setError('Please select a staff member'); return; }
      if (!productTransactionForm.customPrice) { setError('Please enter a price for this product'); return; }
      if (!paynowConfig || !paynowConfig.isActive) {
        setError('PayNow is not configured for this salon. Please contact administrator.');
        return;
      }

      setTabLoading(true);
      setError('');

      const selectedProduct = products.find(p => p.id === productTransactionForm.productId);
      const selectedStaff   = staff.find(s => s.id === productTransactionForm.staffId);
      if (!selectedProduct || !selectedStaff) throw new Error('Selected product or staff not found');

      const qty        = Math.max(1, parseInt(productTransactionForm.quantity) || 1);
      const finalPrice = parseFloat(productTransactionForm.customPrice);

      const transactionData = {
        type:            'product',
        productId:       selectedProduct.id,
        productName:     selectedProduct.name,
        productCategory: selectedProduct.category || '',
        staffId:         selectedStaff.id,
        staffName:       selectedStaff.name,
        quantity:        qty,
        price:           finalPrice.toString(),
        originalPrice:   parseFloat(productTransactionForm.customPrice),
        status:          'pending',
        customerId,
        customerEmail:   customer.email,
        salonId,
        locationId:   productTransactionForm.locationId || '',
        locationName: locations.find(l => l.locationId === productTransactionForm.locationId)?.locationName || '',
        paymentMethod:   'paynow',
        pointsEarned:    0,
        createdAt:       new Date(),
        updatedAt:       new Date()
      };

      const txRef = await addDoc(collection(db, 'transactions'), transactionData);

      // Store product context so handlePayNowVerified can decrement stock
      setPendingProductId(selectedProduct.id);
      setPendingProductQuantity(qty);

      const referenceData = await generatePayNowReference(salonId, txRef.id, finalPrice);

      setPendingTransactionId(txRef.id);
      setPaynowReference(referenceData.reference);
      setPaynowAmount(finalPrice);

      setShowAddProductTransaction(false);
      setActiveVoucher(null);
      setVoucherApplied(false);
      setNettPrice(null);
      setProductTransactionForm({
        productId: '', quantity: 1, staffId: '', customPrice: '',
        paymentMethod: 'cash', paymentStatus: 'Pending', locationId: ''
      });

      setPaynowIsGenerating(true);
      setPaynowQRData(null);
      setPaynowIsDynamic(false);
      setPaynowFallback(false);
      setPaynowFallbackReason('');
      setShowPayNowQR(true);

      try {
        const qrResult = await generateDynamicQR(salonId, txRef.id, finalPrice, referenceData.reference);
        if (qrResult.isDynamic && qrResult.qrCodeData) {
          setPaynowQRData(qrResult.qrCodeData);
          setPaynowIsDynamic(true);
          setPaynowFallback(false);
        } else {
          setPaynowIsDynamic(false);
          setPaynowFallback(true);
          setPaynowFallbackReason(qrResult.fallbackReason || 'Dynamic QR generation unavailable');
        }
      } catch (qrErr) {
        setPaynowIsDynamic(false);
        setPaynowFallback(true);
        setPaynowFallbackReason(qrErr.message || 'Failed to generate dynamic QR code');
      } finally {
        setPaynowIsGenerating(false);
      }

    } catch (err) {
      console.error('Error generating product PayNow QR:', err);
      setError('Failed to generate PayNow QR code. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
    }
  };

  // Handle Generate QR Code for PayNow
  const handleGeneratePayNowQR = async () => {
    try {
      // Validate form
      if (!transactionForm.serviceId) {
        setError('Please select a service');
        return;
      }

      if (!transactionForm.staffId) {
        setError('Please select a staff member');
        return;
      }

      if (transactionForm.customPrice === '' || transactionForm.customPrice === null || transactionForm.customPrice === undefined) {
        setError('Please enter a price for this service');
        return;
      }

      // Check if PayNow is configured
      if (!paynowConfig || !paynowConfig.isActive) {
        setError('PayNow is not configured for this salon. Please contact administrator.');
        return;
      }

      setTabLoading(true);
      setError('');

      // Calculate amount
      const selectedService = services.find(s => s.id === transactionForm.serviceId);
      const selectedStaff = staff.find(s => s.id === transactionForm.staffId);

      if (!selectedService || !selectedStaff) {
        throw new Error('Selected service or staff member not found');
      }

      const finalPrice = parseFloat(transactionForm.customPrice);

      // Create the transaction first
      const transactionData = {
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        staffId: selectedStaff.id,
        staffName: selectedStaff.name,
        price: finalPrice.toString(),
        originalPrice: selectedService.priceMin ?? selectedService.price ?? 0,
        duration: selectedService.duration || '60',
        status: 'pending',
        customerId: customerId,
        customerEmail: customer.email,
        salonId: salonId,
        locationId: transactionForm.locationId || '',
        locationName: locations.find(l => l.locationId === transactionForm.locationId)?.locationName || '',
        paymentMethod: 'paynow',
        pointsEarned: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add to main transactions collection
      const transactionsRef = collection(db, 'transactions');
      const transactionDocRef = await addDoc(transactionsRef, transactionData);

      // Generate PayNow reference
      const referenceData = await generatePayNowReference(
        salonId,
        transactionDocRef.id,
        finalPrice
      );

      // Set PayNow states
      setPendingTransactionId(transactionDocRef.id);
      setPaynowReference(referenceData.reference);
      setPaynowAmount(finalPrice);

      // Close add transaction modal and show QR modal immediately with loading state
      setShowAddTransaction(false);
      setPaynowIsGenerating(true);
      setPaynowQRData(null);
      setPaynowIsDynamic(false);
      setPaynowFallback(false);
      setPaynowFallbackReason('');
      setShowPayNowQR(true);

      // Automatically set payment method to PayNow in the form
      setTransactionForm(prev => ({
        ...prev,
        paymentMethod: 'paynow'
      }));

      // Generate dynamic QR via OCBC API
      try {
        const qrResult = await generateDynamicQR(
          salonId,
          transactionDocRef.id,
          finalPrice,
          referenceData.reference
        );

        if (qrResult.isDynamic && qrResult.qrCodeData) {
          setPaynowQRData(qrResult.qrCodeData);
          setPaynowIsDynamic(true);
          setPaynowFallback(false);
        } else {
          // Fallback to static QR
          setPaynowIsDynamic(false);
          setPaynowFallback(true);
          setPaynowFallbackReason(qrResult.fallbackReason || 'Dynamic QR generation unavailable');
        }
      } catch (qrErr) {
        console.error('Error generating dynamic QR, falling back to static:', qrErr);
        setPaynowIsDynamic(false);
        setPaynowFallback(true);
        setPaynowFallbackReason(qrErr.message || 'Failed to generate dynamic QR code');
      } finally {
        setPaynowIsGenerating(false);
      }

    } catch (err) {
      console.error('Error generating PayNow QR:', err);
      setError('Failed to generate PayNow QR code. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
    }
  };

  // Handle PayNow payment verification callback
  const handlePayNowVerified = async (transactionId) => {
    try {
      setSuccessMessage('Payment verified successfully!');

      // Recalculate totalSpent (sum of all transaction totalAmounts)
      await updateCustomerTotalSpent(customerId, customer.email, salonId);
      console.log('Customer totalSpent recalculated after PayNow verification');

      // Recalculate totalSpentLast365Days (sum of totalAmounts in last 365 days)
      await updateCustomerTotalSpentLast365Days(customerId, customer.email, salonId);
      console.log('Customer totalSpentLast365Days recalculated after PayNow verification');

      // Recalculate loyalty tier based on points in last 365 days
      await updateCustomerLoyaltyTier(customerId, customer.email, salonId);
      console.log('Customer loyalty tier recalculated after PayNow verification');

      // Auto-decrement product stock if this was a product PayNow payment
      if (pendingProductId) {
        try {
          const productRef  = doc(db, 'products', pendingProductId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const currentStock = productSnap.data().stockLevel;
            if (currentStock !== null && currentStock !== undefined) {
              const newStock = Math.max(0, Number(currentStock) - pendingProductQuantity);
              await updateDoc(productRef, {
                stockLevel:     newStock,
                stockUpdatedAt: new Date(),
                updatedAt:      new Date()
              });
            }
          }
        } catch (stockErr) {
          console.error('Stock decrement after product PayNow failed (non-blocking):', stockErr);
        }
        setPendingProductId(null);
        setPendingProductQuantity(1);
      }

      // Reload transactions
      await loadTransactions();

      // Reset form
      setTransactionForm({
        serviceId: '',
        staffId: '',
        customPrice: '',
        paymentMethod: 'cash',
        paymentStatus: 'Pending'
      });

      // Close QR modal and reset all PayNow states
      setShowPayNowQR(false);
      setPendingTransactionId(null);
      setPaynowReference(null);
      setPaynowQRData(null);
      setPaynowIsDynamic(false);
      setPaynowFallback(false);
      setPaynowFallbackReason('');
      setPaynowIsGenerating(false);

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error after payment verification:', err);
    }
  };

  // Handle Try Again - regenerate dynamic QR for the same transaction
  const handleTryAgain = async () => {
    if (!pendingTransactionId || !paynowReference) return;

    try {
      setPaynowIsGenerating(true);
      setPaynowQRData(null);
      setPaynowIsDynamic(false);
      setPaynowFallback(false);
      setPaynowFallbackReason('');

      const qrResult = await generateDynamicQR(
        salonId,
        pendingTransactionId,
        paynowAmount,
        paynowReference
      );

      if (qrResult.isDynamic && qrResult.qrCodeData) {
        setPaynowQRData(qrResult.qrCodeData);
        setPaynowIsDynamic(true);
        setPaynowFallback(false);
      } else {
        setPaynowIsDynamic(false);
        setPaynowFallback(true);
        setPaynowFallbackReason(qrResult.fallbackReason || 'Dynamic QR generation unavailable');
      }
    } catch (err) {
      console.error('Error regenerating dynamic QR:', err);
      setPaynowIsDynamic(false);
      setPaynowFallback(true);
      setPaynowFallbackReason(err.message || 'Failed to regenerate dynamic QR code');
    } finally {
      setPaynowIsGenerating(false);
    }
  };

  // Handle Cancel Transaction
  const handleCancelTransaction = async () => {
    if (!pendingTransactionId) return;

    const confirmCancel = window.confirm(
      'Are you sure you want to cancel this transaction? This action cannot be undone.'
    );
    if (!confirmCancel) return;

    try {
      await cancelPayNowTransaction(pendingTransactionId, salonId);

      // Close modal and reset all PayNow states
      setShowPayNowQR(false);
      setPendingTransactionId(null);
      setPaynowReference(null);
      setPaynowQRData(null);
      setPaynowIsDynamic(false);
      setPaynowFallback(false);
      setPaynowFallbackReason('');
      setPaynowIsGenerating(false);
      setPaynowAmount(0);

      // Reset form
      setTransactionForm({
        serviceId: '',
        staffId: '',
        customPrice: '',
        paymentMethod: 'cash',
        paymentStatus: 'Pending'
      });

      // Reload transactions to reflect cancelled status
      await loadTransactions();

      setSuccessMessage('Transaction cancelled successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error cancelling transaction:', err);
      setError('Failed to cancel transaction. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Delete an appointment record
  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'appointments', appointmentId));
      setAppointments(prev => prev.filter(a => a.id !== appointmentId));
      setSuccessMessage('Appointment deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Delete a voucher record
  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm('Are you sure you want to delete this voucher? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'vouchers', voucherId));
      setSuccessMessage('Voucher deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting voucher:', err);
      setError('Failed to delete voucher. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Delete a transaction record
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'transactions', transactionId));
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      setSuccessMessage('Transaction deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Check if stylist is on leave for a given date
  const checkStylistLeave = async (staffId, date) => {
    try {
      const leaveRef = collection(db, 'leave');
      const leaveQuery = query(
        leaveRef,
        where('staffId', '==', staffId),
        where('status', '==', 'approved')
      );
      const leaveSnapshot = await getDocs(leaveQuery);

      const selectedDate = new Date(date + 'T00:00:00');

      for (const doc of leaveSnapshot.docs) {
        const leaveData = doc.data();
        const startDate = leaveData.startDate.toDate();
        const endDate = leaveData.endDate.toDate();

        if (selectedDate >= startDate && selectedDate <= endDate) {
          return {
            isOnLeave: true,
            timeType: leaveData.timeType || 'full',
            reason: leaveData.reason || 'On leave',
            type: leaveData.type || 'leave'
          };
        }
      }

      return { isOnLeave: false, timeType: null, reason: null, type: null };
    } catch (error) {
      console.error('Error checking stylist leave:', error);
      return { isOnLeave: false, timeType: null, reason: null, type: null };
    }
  };

  // Fetch this staff member's approved appointments for a given date, with
  // computed start/end Date objects (used for the double-booking guard below).
  const getStaffApprovedAppointmentsForDate = async (staffId, dateStr) => {
    try {
      const apptRef = collection(db, 'appointments');
      const apptQuery = query(
        apptRef,
        where('staffId', '==', staffId),
        where('status', '==', 'approved')
      );
      const snapshot = await getDocs(apptQuery);
      const dayStart = new Date(dateStr + 'T00:00:00');
      const dayEnd = new Date(dateStr + 'T23:59:59');

      return snapshot.docs
        .map(d => d.data())
        .map(appt => {
          const start = appt.dateTimeRequested?.toDate
            ? appt.dateTimeRequested.toDate()
            : new Date(appt.dateTimeRequested);
          const duration = appt.serviceRequested?.duration || appt.duration || 60;
          const end = new Date(start);
          end.setMinutes(end.getMinutes() + duration);
          return { ...appt, start, end };
        })
        .filter(appt => appt.start >= dayStart && appt.start <= dayEnd);
    } catch (error) {
      console.error('Error checking staff appointments:', error);
      return [];
    }
  };

  // Does `slotStart`-`slotEnd` conflict with this staff's existing bookings for the day,
  // given the optional service the customer is currently trying to book?
  const findSlotConflict = (staffId, slotStart, slotEnd, existingAppointments, requestedServiceId = null) => {
    const overlapping = existingAppointments.filter(appt => rangesOverlap(slotStart, slotEnd, appt.start, appt.end));
    if (overlapping.length === 0) return null;

    const staffObj = staff.find(s => s.id === staffId);
    const requestedService = requestedServiceId ? services.find(s => s.id === requestedServiceId) : null;
    const overlappingServices = overlapping
      .map(appt => services.find(s => s.id === appt.serviceId))
      .filter(Boolean);

    if (canShareSlot(staffObj, overlappingServices, requestedService)) return null;

    return overlapping[0];
  };

  // Generate available time slots based on staff working hours roster and stylist availability
  const generateTimeSlots = async () => {
    if (!appointmentForm.dateRequested || !appointmentForm.staffId) return;

    try {
      const dateStr = appointmentForm.dateRequested;

      // Block if salon is closed for a Public Holiday
      const holidayMatch = holidayClosures.find(h => h && h.date === dateStr);
      if (holidayMatch) {
        setAvailableTimeSlots([]);
        setValidationMessage(`⚠️ The salon is closed on this day for ${holidayMatch.name || 'a Public Holiday'}. Please select a different date.`);
        return;
      }

      // Block if salon is closed for a Custom Closure
      const dateObj = new Date(dateStr + 'T00:00:00');
      dateObj.setHours(0, 0, 0, 0);
      const customMatch = customClosures.find(c => {
        if (!c || !c.startDate || !c.endDate) return false;
        const start = new Date(c.startDate + 'T00:00:00'); start.setHours(0, 0, 0, 0);
        const end   = new Date(c.endDate   + 'T00:00:00'); end.setHours(23, 59, 59, 999);
        return dateObj >= start && dateObj <= end;
      });
      if (customMatch) {
        setAvailableTimeSlots([]);
        setValidationMessage(`⚠️ The salon is closed on this day (${customMatch.reason || 'Custom Closure'}). Please select a different date.`);
        return;
      }

      // Check if stylist is on leave
      const leaveInfo = await checkStylistLeave(appointmentForm.staffId, dateStr);

      if (leaveInfo.isOnLeave && leaveInfo.timeType === 'full') {
        setAvailableTimeSlots([]);
        setValidationMessage('⚠️ Selected staff member is on leave on this day. Please select another date or staff member.');
        return;
      }

      // Fetch this staff member's existing bookings so slots can be marked unavailable
      // (the service isn't chosen yet at this point, so this is the general "does the
      // staff still have room here" check — the specific service is re-checked on submit).
      const existingAppointments = await getStaffApprovedAppointmentsForDate(appointmentForm.staffId, dateStr);

      // Query the workingHours collection for this staff member
      const whRef = collection(db, 'workingHours');
      const whQuery = query(
        whRef,
        where('staffId', '==', appointmentForm.staffId),
        where('salonId', '==', salonId)
      );
      const whSnapshot = await getDocs(whQuery);

      // Fulltime staff have no roster requirement — they work all salon hours by default
      let isFulltime = true;

      if (!whSnapshot.empty) {
        const whData = whSnapshot.docs[0].data();
        isFulltime = (whData.employmentType || 'fulltime') === 'fulltime';

        // Check off day(s) — blocks the entire date
        const rawOffDay = whData.offDay;
        const offDays = Array.isArray(rawOffDay) ? rawOffDay : (rawOffDay ? [rawOffDay] : []);
        const selectedDayNameForOffDay = new Date(appointmentForm.dateRequested + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
        if (offDays.includes(selectedDayNameForOffDay)) {
          setAvailableTimeSlots([]);
          setValidationMessage('⚠️ Selected staff member is not available on this day (off day). Please select another date or staff member.');
          return;
        }

        const roster = whData.roster || {};

        const dateKey = appointmentForm.dateRequested;
        const rosterSlots = roster[dateKey] || [];

        if (rosterSlots.length > 0) {
          const sortedSlots = [...rosterSlots].sort();
          const slots = sortedSlots
            .filter(timeString => {
              const slotStart = new Date(`${dateStr}T${timeString}`);
              const slotEnd = new Date(slotStart);
              slotEnd.setMinutes(slotEnd.getMinutes() + 30);
              return !findSlotConflict(appointmentForm.staffId, slotStart, slotEnd, existingAppointments);
            })
            .map(timeString => ({
              value: timeString,
              label: timeString,
              available: true
            }));

          setAvailableTimeSlots(slots);
          setValidationMessage(slots.length === 0 ? '⚠️ No available time slots for this staff member on this date.' : '');
          return;
        }
      }

      // Fallback: use salon business hours
      const selectedDayName = new Date(appointmentForm.dateRequested + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
      const dayHours = businessHours.find(day => day.day === selectedDayName);

      if (!dayHours || dayHours.isClosed) {
        setAvailableTimeSlots([]);
        setValidationMessage('⚠️ No working hours set for this staff member on this date and salon is closed.');
        return;
      }

      // Generate time slots from salon business hours as fallback
      const [openHour, openMinute] = dayHours.open.split(':').map(Number);
      const [closeHour, closeMinute] = dayHours.close.split(':').map(Number);

      const startTime = openHour * 60 + openMinute;
      const endTime = closeHour * 60 + closeMinute;
      const slots = [];
      const slotInterval = 30;

      for (let time = startTime; time < endTime; time += slotInterval) {
        const hours = Math.floor(time / 60);
        const minutes = time % 60;
        const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

        const slotStart = new Date(`${dateStr}T${timeString}`);
        const slotEnd = new Date(slotStart);
        slotEnd.setMinutes(slotEnd.getMinutes() + slotInterval);

        if (findSlotConflict(appointmentForm.staffId, slotStart, slotEnd, existingAppointments)) continue;

        slots.push({
          value: timeString,
          label: timeString,
          available: true
        });
      }

      setAvailableTimeSlots(slots);
      // Only show the info notice for non-fulltime staff who have no roster set for this date
      setValidationMessage(
        slots.length === 0
          ? '⚠️ No available time slots for this staff member on this date.'
          : (isFulltime ? '' : 'ℹ️ No roster set for this staff on this date. Showing salon business hours.')
      );
    } catch (error) {
      console.error('Error generating time slots:', error);
      setAvailableTimeSlots([]);
      setValidationMessage('');
    }
  };

  const handleAddAppointment = async () => {
    try {
      if (!appointmentForm.dateRequested) {
        setError('Please select a date');
        return;
      }

      if (!appointmentForm.timeRequested) {
        setError('Please select a time');
        return;
      }

      if (!appointmentForm.staffId) {
        setError('Please select a staff member');
        return;
      }

      if (appointmentForm.appointmentType === 'package' && !appointmentForm.packageId) {
        setError('Please select a package');
        return;
      }

      if (appointmentForm.appointmentType === 'service' && !appointmentForm.serviceId) {
        setError('Please select a service');
        return;
      }

      // Closure guard — prevent booking on Public Holidays or Custom Closures
      const dateStr = appointmentForm.dateRequested;
      const holidayMatch = holidayClosures.find(h => h && h.date === dateStr);
      if (holidayMatch) {
        setError(`Cannot book on ${dateStr}: the salon is closed for ${holidayMatch.name || 'a Public Holiday'}.`);
        return;
      }
      const dateObj = new Date(dateStr + 'T00:00:00');
      dateObj.setHours(0, 0, 0, 0);
      const customMatch = customClosures.find(c => {
        if (!c || !c.startDate || !c.endDate) return false;
        const start = new Date(c.startDate + 'T00:00:00'); start.setHours(0, 0, 0, 0);
        const end   = new Date(c.endDate   + 'T00:00:00'); end.setHours(23, 59, 59, 999);
        return dateObj >= start && dateObj <= end;
      });
      if (customMatch) {
        setError(`Cannot book on ${dateStr}: the salon is closed (${customMatch.reason || 'Custom Closure'}).`);
        return;
      }

      setTabLoading(true);
      setError('');

      const selectedStaff = staff.find(s => s.id === appointmentForm.staffId);
      if (!selectedStaff) throw new Error('Selected staff member not found');

      // Resolve service: for package bookings use the first service in the package definition
      let selectedService = null;
      let bookingPackageId = null;
      let bookingPackageName = null;
      if (appointmentForm.appointmentType === 'package') {
        const custPkg = customerPackages.find(p => p.id === appointmentForm.packageId);
        if (!custPkg) throw new Error('Selected package not found');
        bookingPackageId = custPkg.id;
        bookingPackageName = custPkg.packageName;
        const pkgDef = custPkg.packageDefinition || {};
        const firstSvcEntry = Array.isArray(pkgDef.services) && pkgDef.services[0];
        if (firstSvcEntry) {
          selectedService = services.find(s => s.id === firstSvcEntry.serviceId) || {
            id: firstSvcEntry.serviceId || '',
            name: firstSvcEntry.serviceName || custPkg.packageName,
            duration: pkgDef.durationMinutes || 60,
            price: pkgDef.price || 0,
          };
        } else {
          // Fallback: use package-level name/duration
          selectedService = {
            id: '',
            name: custPkg.packageName,
            duration: pkgDef.durationMinutes || 60,
            price: pkgDef.price || 0,
          };
        }
      } else {
        selectedService = services.find(s => s.id === appointmentForm.serviceId);
        if (!selectedService) throw new Error('Selected service not found');
      }

      // Combine date and time into a single datetime
      const requestedDateTime = new Date(`${appointmentForm.dateRequested}T${appointmentForm.timeRequested}`);

      const customerName = customer.displayName || customer.name || customer.email?.split('@')[0] || 'Unknown';
      const servicePrice    = selectedService.priceMin ?? selectedService.price ?? 0;
      const serviceDuration = Number(selectedService.duration) || 60;

      // Final double-booking guard
      const requestedEnd = new Date(requestedDateTime);
      requestedEnd.setMinutes(requestedEnd.getMinutes() + serviceDuration);
      const existingAppointmentsForSubmit = await getStaffApprovedAppointmentsForDate(selectedStaff.id, dateStr);
      const conflict = findSlotConflict(selectedStaff.id, requestedDateTime, requestedEnd, existingAppointmentsForSubmit, selectedService.id);
      if (conflict) {
        setTabLoading(false);
        setError(`${selectedStaff.name} already has an appointment in this slot (${conflict.serviceName || conflict.serviceRequested?.name || 'another service'}), and "${selectedService.name}" does not allow sharing this time slot.`);
        return;
      }

      const appointmentData = {
        customerEmail: customer.email,
        customerId: customerId,
        customerName,
        salonId: salonId,
        locationId: appointmentForm.locationId || '',
        staffId: selectedStaff.id,
        staffName: selectedStaff.name,
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        price: servicePrice,
        duration: serviceDuration,
        serviceRequested: {
          id: selectedService.id,
          name: selectedService.name,
          duration: serviceDuration,
          price: servicePrice,
        },
        ...(bookingPackageId && { packageId: bookingPackageId, packageName: bookingPackageName }),
        dateTimeRequested: requestedDateTime,
        dateTimeCreated: new Date(),
        appointmentDate: requestedDateTime,
        status: 'approved',
        notes: appointmentForm.notes || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add to main appointments collection
      const appointmentsRef = collection(db, 'appointments');
      await addDoc(appointmentsRef, appointmentData);
      
      setSuccessMessage('Appointment added successfully!');
      setShowAddAppointment(false);
      
      setAppointmentForm({
        dateRequested: '',
        timeRequested: '',
        staffId: '',
        serviceId: '',
        packageId: '',
        appointmentType: 'service',
        notes: '',
        locationId: ''
      });

      await loadAppointments();
      
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error adding appointment:', err);
      setError('Failed to add appointment. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
    }
  };

  const handleEditTransaction = (transaction) => {
    // Try to get serviceId from transaction, verify it exists in current services list
    let serviceId = transaction.serviceId || '';
    const serviceExistsById = serviceId && services.some(s => s.id === serviceId);

    // If serviceId doesn't exist or doesn't match any current service, try to find by name
    if (!serviceExistsById && transaction.serviceName) {
      const matchedService = services.find(s =>
        s.name.toLowerCase() === transaction.serviceName.toLowerCase()
      );
      if (matchedService) {
        serviceId = matchedService.id;
      }
    }

    // Try to get staffId from transaction, verify it exists in current staff list
    let staffId = transaction.staffId || '';
    const staffExistsById = staffId && staff.some(s => s.id === staffId);

    // If staffId doesn't exist or doesn't match any current staff, try to find by name
    if (!staffExistsById && transaction.staffName) {
      const matchedStaff = staff.find(s =>
        s.name.toLowerCase() === transaction.staffName.toLowerCase()
      );
      if (matchedStaff) {
        staffId = matchedStaff.id;
      }
    }

    if (transaction.type === 'product') {
      // Try to find productId in current products list
      let productId = transaction.productId || '';
      if (!productId && transaction.productName) {
        const matchedProduct = products.find(p =>
          p.name.toLowerCase() === transaction.productName.toLowerCase()
        );
        if (matchedProduct) productId = matchedProduct.id;
      }

      setEditingTransaction({
        id: transaction.id,
        type: 'product',
        productId,
        productName: transaction.productName || '',
        quantity: transaction.quantity || 1,
        staffId,
        staffName: transaction.staffName || '',
        customPrice: transaction.price || '',
        paymentMethod: transaction.paymentMethod || 'cash',
        paymentStatus: transaction.paymentStatus || 'Pending',
        previousPaymentStatus: transaction.paymentStatus || 'Pending',
        previousPointsEarned: transaction.pointsEarned || 0,
        locationId: transaction.locationId || ''
      });
    } else {
      setEditingTransaction({
        id: transaction.id,
        type: 'service',
        serviceId: serviceId,
        serviceName: transaction.serviceName || '',
        staffId: staffId,
        staffName: transaction.staffName || '',
        customPrice: transaction.price || '',
        paymentMethod: transaction.paymentMethod || 'cash',
        paymentStatus: transaction.paymentStatus || 'Pending',
        previousPaymentStatus: transaction.paymentStatus || 'Pending',
        previousPointsEarned: transaction.pointsEarned || 0
      });
    }
    setShowEditTransaction(true);
  };

  const handleUpdateTransaction = async () => {
    // #Revised UI_v2 — synchronous guard prevents duplicate notifications from rapid double-click
    if (isUpdatingTransactionRef.current) return;
    isUpdatingTransactionRef.current = true;

    try {
      if (editingTransaction.type === 'product') {
        if (!editingTransaction.productId) {
          setError('Please select a product');
          isUpdatingTransactionRef.current = false;
          return;
        }
        if (!editingTransaction.staffId) {
          setError('Please select a staff member');
          isUpdatingTransactionRef.current = false;
          return;
        }

        setTabLoading(true);
        setError('');

        const selectedProduct = products.find(p => p.id === editingTransaction.productId);
        const selectedStaff = staff.find(s => s.id === editingTransaction.staffId);

        if (!selectedProduct) throw new Error('Selected product not found');
        if (!selectedStaff) throw new Error('Selected staff member not found');

        const qty = Math.max(1, parseInt(editingTransaction.quantity) || 1);
        const finalPrice = parseFloat(editingTransaction.customPrice) || 0;

        const pointsPerCurrency = loyaltyConfig?.pointsPerCurrency || 10;
        const currencyPerPoint = loyaltyConfig?.currencyPerPoint || 10;
        const isNowCompleted = editingTransaction.paymentStatus === 'Completed';
        const wasCompleted = editingTransaction.previousPaymentStatus === 'Completed';
        const newPointsEarned = (isNowCompleted && editingTransaction.paymentMethod !== 'Points')
          ? Math.floor((finalPrice / currencyPerPoint) * pointsPerCurrency)
          : 0;

        const TAX_RATE = 0.09;
        const taxAmount = parseFloat((finalPrice * TAX_RATE).toFixed(2));
        const totalAmount = parseFloat((finalPrice + taxAmount).toFixed(2));

        const updateData = {
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          staffId: selectedStaff.id,
          staffName: selectedStaff.name,
          quantity: qty,
          price: finalPrice.toString(),
          amount: finalPrice.toString(),
          taxAmount,
          totalAmount,
          paymentMethod: editingTransaction.paymentMethod,
          paymentStatus: editingTransaction.paymentStatus,
          pointsEarned: newPointsEarned,
          status: isNowCompleted ? 'paid' : 'pending',
          updatedAt: new Date(),
          ...(isNowCompleted && !wasCompleted ? { paidAt: new Date() } : {})
        };

        const transactionRef = doc(db, 'transactions', editingTransaction.id);
        await updateDoc(transactionRef, updateData);

        if (isNowCompleted && !wasCompleted) {
          sendPaymentReceiptEmail(finalPrice, editingTransaction.id);
          sendPaymentPushNotification(finalPrice);
        }

        const previousPoints = editingTransaction.previousPointsEarned || 0;
        const pointsDelta = newPointsEarned - previousPoints;
        const customerRef = doc(db, 'customers', customerId);
        if (pointsDelta !== 0 || (isNowCompleted && !wasCompleted)) {
          await updateDoc(customerRef, {
            ...(pointsDelta !== 0 ? {
              'metrics.pointsEarned': increment(pointsDelta),
              'metrics.pointsBalance': increment(pointsDelta)
            } : {}),
            ...(isNowCompleted && !wasCompleted ? { 'metrics.lastTransaction': new Date() } : {}),
            updatedAt: new Date()
          });
        }

        if (isNowCompleted && !wasCompleted && newPointsEarned > 0) {
          const currentBalance = customer?.metrics?.pointsBalance || 0;
          sendLoyaltyPointsEmail(newPointsEarned, currentBalance + pointsDelta);
          sendPointsEarnedPushNotification(newPointsEarned, currentBalance + pointsDelta);
        }

        await updateCustomerTotalSpent(customerId, customer.email, salonId);
        await updateCustomerTotalSpentLast365Days(customerId, customer.email, salonId);
        await updateCustomerLoyaltyTier(customerId, customer.email, salonId);

        setSuccessMessage('Transaction updated successfully!');
        setShowEditTransaction(false);
        setEditingTransaction(null);
        await loadTransactions();
        await loadCustomerData();
        setTimeout(() => setSuccessMessage(''), 3000);
        return;
      }

      // Service transaction
      if (!editingTransaction.serviceId) {
        setError('Please select a service');
        isUpdatingTransactionRef.current = false;
        return;
      }

      if (!editingTransaction.staffId) {
        setError('Please select a staff member');
        isUpdatingTransactionRef.current = false;
        return;
      }

      setTabLoading(true);
      setError('');

      const selectedService = services.find(s => s.id === editingTransaction.serviceId);
      const selectedStaff = staff.find(s => s.id === editingTransaction.staffId);

      if (!selectedService) {
        throw new Error('Selected service not found');
      }

      if (!selectedStaff) {
        throw new Error('Selected staff member not found');
      }

      const finalPrice = parseFloat(editingTransaction.customPrice) || 0;

      // Calculate points earned using loyalty program settings
      const pointsPerCurrency = loyaltyConfig?.pointsPerCurrency || 10;
      const currencyPerPoint = loyaltyConfig?.currencyPerPoint || 10;
      const isNowCompleted = editingTransaction.paymentStatus === 'Completed';
      const wasCompleted = editingTransaction.previousPaymentStatus === 'Completed';
      const newPointsEarned = (isNowCompleted && editingTransaction.paymentMethod !== 'Points')
        ? Math.floor((finalPrice / currencyPerPoint) * pointsPerCurrency)
        : 0;

      const updateData = {
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        staffId: selectedStaff.id,
        staffName: selectedStaff.name,
        price: finalPrice.toString(),
        originalPrice: selectedService.priceMin ?? selectedService.price ?? 0,
        paymentMethod: editingTransaction.paymentMethod,
        paymentStatus: editingTransaction.paymentStatus,
        pointsEarned: newPointsEarned,
        status: isNowCompleted ? 'paid' : 'pending',
        updatedAt: new Date(),
        ...(isNowCompleted && !wasCompleted ? { paidAt: new Date() } : {})
      };

      // Update in main transactions collection
      const transactionRef = doc(db, 'transactions', editingTransaction.id);
      await updateDoc(transactionRef, updateData);

      // Send payment receipt email and push notification when status changes to Completed (non-blocking)
      if (isNowCompleted && !wasCompleted) {
        sendPaymentReceiptEmail(finalPrice, editingTransaction.id);
        sendPaymentPushNotification(finalPrice);
      }

      // Adjust customer points balance and set lastTransaction when becoming Completed
      const previousPoints = editingTransaction.previousPointsEarned || 0;
      const pointsDelta = newPointsEarned - previousPoints;
      const customerRef = doc(db, 'customers', customerId);
      if (pointsDelta !== 0 || (isNowCompleted && !wasCompleted)) {
        await updateDoc(customerRef, {
          ...(pointsDelta !== 0 ? {
            'metrics.pointsEarned': increment(pointsDelta),
            'metrics.pointsBalance': increment(pointsDelta)
          } : {}),
          // #Revised UI_v2 — set lastTransaction timestamp when payment completes
          ...(isNowCompleted && !wasCompleted ? {
            'metrics.lastTransaction': new Date()
          } : {}),
          updatedAt: new Date()
        });
        if (pointsDelta !== 0) console.log(`Adjusted customer points by ${pointsDelta} (was ${previousPoints}, now ${newPointsEarned})`);
        if (isNowCompleted && !wasCompleted) console.log('Set metrics.lastTransaction to now');
      }

      // Send loyalty points email and push notification when transaction changes to Completed
      if (isNowCompleted && !wasCompleted && newPointsEarned > 0) {
        const currentBalance = customer?.metrics?.pointsBalance || 0;
        sendLoyaltyPointsEmail(newPointsEarned, currentBalance + pointsDelta);
        sendPointsEarnedPushNotification(newPointsEarned, currentBalance + pointsDelta);
      }

      // Recalculate totalSpent (sum of all transaction totalAmounts)
      await updateCustomerTotalSpent(customerId, customer.email, salonId);
      console.log('Customer totalSpent recalculated after transaction update');

      // Recalculate totalSpentLast365Days (sum of totalAmounts in last 365 days)
      await updateCustomerTotalSpentLast365Days(customerId, customer.email, salonId);
      console.log('Customer totalSpentLast365Days recalculated after transaction update');

      // Recalculate loyalty tier based on points in last 365 days
      await updateCustomerLoyaltyTier(customerId, customer.email, salonId);
      console.log('Customer loyalty tier recalculated after transaction update');

      setSuccessMessage('Transaction updated successfully!');
      setShowEditTransaction(false);
      setEditingTransaction(null);

      // #Revised UI_v2 — reload both transactions and customer data so all metrics refresh in UI
      await loadTransactions();
      await loadCustomerData();

      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('Error updating transaction:', err);
      setError('Failed to update transaction. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setTabLoading(false);
      isUpdatingTransactionRef.current = false; // #Revised UI_v2 — release guard
    }
  };

  const handleRedeemVoucher = async (voucher) => {
    const confirmRedeem = window.confirm(
      'This marks the voucher as redeemed without creating a sale.\n\n' +
      'Please remember to add the corresponding transaction for this customer separately.\n\n' +
      'Continue redeeming this voucher?'
    );
    if (!confirmRedeem) return;

    try {
      setTabLoading(true);
      setError('');

      console.log('Redeeming voucher:', voucher);

      const redeemedAt = new Date();

      // Try to auto-link this redemption to a same-day completed transaction for
      // this customer, mirroring backend/scripts/backfill-voucher-redeemed-transaction-links.js,
      // so a manual redemption doesn't have to wait on that offline script to get
      // a redeemedTransactionId. A transaction already claimed by another one of
      // this customer's vouchers is never reassigned; if there's more than one
      // same-day candidate, the one closest in time to redemption wins.
      const claimedTransactionIds = new Set(
        vouchers
          .filter(v => v.id !== voucher.id && v.redeemedTransactionId)
          .map(v => v.redeemedTransactionId)
      );

      const sameDayMatches = transactions
        .filter(t => t.paymentStatus === 'Completed' && !claimedTransactionIds.has(t.id))
        .map(t => ({
          t,
          txnDate: t.paidAt instanceof Date ? t.paidAt : (t.createdAt instanceof Date ? t.createdAt : null)
        }))
        .filter(({ txnDate }) => txnDate && txnDate.toDateString() === redeemedAt.toDateString())
        .map(({ t, txnDate }) => ({ t, diff: Math.abs(txnDate.getTime() - redeemedAt.getTime()) }))
        .sort((a, b) => a.diff - b.diff);

      const matchedTransaction = sameDayMatches.length > 0 ? sameDayMatches[0].t : null;

      const voucherRef = doc(db, 'vouchers', voucher.id);
      const voucherUpdateData = {
        status: 'redeemed',
        redeemedAt,
        updatedAt: redeemedAt,
        ...(matchedTransaction && {
          redeemedTransactionId: matchedTransaction.id,
          redeemedTransactionIdInferred: true,
          redeemedTransactionIdInferredAt: redeemedAt
        })
      };

      await updateDoc(voucherRef, voucherUpdateData);
      console.log(
        'Updated voucher status to redeemed',
        matchedTransaction ? `(linked to transaction ${matchedTransaction.id})` : '(no same-day transaction found to link yet)'
      );

      if (voucher.promotionId) {
        try {
          const promotionRef = doc(db, 'promotions', voucher.promotionId);
          
          const promotionDoc = await getDoc(promotionRef);
          
          if (promotionDoc.exists()) {
            const promotionData = promotionDoc.data();
            const currentRedemptions = promotionData.currentRedemptions || 0;
            const newRedemptionCount = currentRedemptions + 1;
            
            if (promotionData.maxRedemptions && newRedemptionCount > promotionData.maxRedemptions) {
              throw new Error(`Maximum redemptions (${promotionData.maxRedemptions}) exceeded for this promotion`);
            }
            
            await updateDoc(promotionRef, {
              currentRedemptions: newRedemptionCount,
              updatedAt: new Date()
            });
            
            console.log(`Updated promotion ${voucher.promotionId} currentRedemptions: ${currentRedemptions} → ${newRedemptionCount}`);
            
            if (promotionData.maxRedemptions && newRedemptionCount >= promotionData.maxRedemptions) {
              await updateDoc(promotionRef, {
                isActive: false,
                deactivatedReason: 'Maximum redemptions reached',
                deactivatedAt: new Date()
              });
              console.log(`Deactivated promotion ${voucher.promotionId} - max redemptions reached`);
            }
            
          } else {
            console.warn(`Promotion document ${voucher.promotionId} not found`);
          }
          
        } catch (promotionError) {
          console.error('Error updating promotion redemption count:', promotionError);
          setError(`Voucher redeemed successfully, but there was an issue updating promotion statistics: ${promotionError.message}`);
        }
      } else {
        console.warn('No promotionId found in voucher - cannot update promotion redemption count');
      }

      // Immediately update local state so admin UI reflects the change without
      // waiting for the onSnapshot callback
      setVouchers(prev => prev.map(v =>
        v.id === voucher.id
          ? { ...v, ...voucherUpdateData }
          : v
      ));

      setSuccessMessage(
        matchedTransaction
          ? 'Voucher redeemed successfully and linked to a transaction from today.'
          : 'Voucher redeemed successfully! Remember to add the corresponding transaction for this customer.'
      );

      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('Error redeeming voucher:', err);
      setError(`Failed to redeem voucher: ${err.message}. Please try again.`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setTabLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      const dateObj = date instanceof Date ? date : 
                    date.seconds ? new Date(date.seconds * 1000) : 
                    new Date(date);
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    let numericAmount = 0;

    if (amount === null || amount === undefined || amount === '') {
      numericAmount = 0;
    } else if (typeof amount === 'string') {
      numericAmount = parseFloat(amount) || 0;
    } else if (typeof amount === 'number') {
      numericAmount = amount;
    } else {
      numericAmount = 0;
    }

    return `$${numericAmount.toFixed(2)}`;
  };

  const formatServicePriceRange = (service) => {
    if (service.priceMin !== undefined && service.priceMax !== undefined) {
      if (service.priceMin === service.priceMax) return `$${Number(service.priceMin).toFixed(2)}`;
      return `$${Number(service.priceMin).toFixed(2)} – $${Number(service.priceMax).toFixed(2)}`;
    }
    return formatCurrency(service.price);
  };

  // Points redemption helpers
  const getPointsRequired = () => {
    if (!transactionForm.serviceId) return 0;
    const selectedService = services.find(s => s.id === transactionForm.serviceId);
    if (!selectedService) return 0;

    const finalPrice = parseFloat(transactionForm.customPrice) || 0;

    const pointValue = loyaltyConfig?.pointValue || 0.1;
    return Math.ceil(finalPrice / pointValue);
  };

  const canRedeemWithPoints = () => {
    const pointsRequired = getPointsRequired();
    if (pointsRequired <= 0) return false;
    const pointsBalance = customer?.metrics?.pointsBalance || 0;
    return pointsBalance >= pointsRequired;
  };

  const canPayWithWallet = () => {
    if (!transactionForm.serviceId) return false;
    const finalPrice = (activeVoucher && voucherApplied && nettPrice !== null)
      ? nettPrice
      : (parseFloat(transactionForm.customPrice) || 0);
    if (finalPrice <= 0) return false;
    const walletBalance = customer?.metrics?.walletBalance || 0;
    return walletBalance >= finalPrice;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'approved': 'status-confirmed',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled',
      'rejected': 'status-rejected',
      'paid': 'status-paid',
      'pending payment': 'status-pending',
      'unpaid': 'status-unpaid',
      'active': 'status-active',
      'redeemed': 'status-redeemed',
      'expired': 'status-expired'
    };

    return (
      <span className={`status-badge ${statusClasses[status?.toLowerCase()] || 'status-unknown'}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  // Debug render info
  console.log('RENDER DEBUG:', {
    loading,
    customer: !!customer,
    activeTab,
    tabLoading,
    appointmentsLength: appointments.length,
    vouchersLength: vouchers.length,
    transactionsLength: transactions.length
  });

  if (loading && !customer) {
    return (
      <div className="customer-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer && !loading) {
    return (
      <div className="customer-details-page">
        <div className="error-container">
          <h2>Customer Not Found</h2>
          <p>The requested customer could not be found.</p>
          <button onClick={() => navigate(`/BespokeBeauty/${salonId}/admin/core`, { state: { activeTab: 'customers' } })} className="back-button">
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-details-page">
      <div className="customer-details-header">
        <div className="header-controls">
          <button onClick={() => navigate(`/BespokeBeauty/${salonId}/admin/core`, { state: { activeTab: 'customers' } })} className="back-button">
            ← Back to Customers
          </button>
        </div>
        
        <div className="customer-summary">
          <div className="customer-avatar" style={{ marginLeft: '16px' }}>
            {customer.photoURL ? (
              <img src={customer.photoURL} alt={customer.displayName} />
            ) : (
              <div className="avatar-placeholder">
                {(customer.displayName || 'U').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="customer-info cdp-info-layout" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: 'relative' }}>
            <div className="cdp-name-block" style={{ marginLeft: '0px' }}>
              <div className="cdp-name-tier-row">
                <h1>{customer.displayName || 'Unknown Customer'}</h1>
                <span className={`loyalty-tier ${(customer.loyaltyTier || 'bronze').toLowerCase()}`}>
                  {customer.loyaltyTier || 'Bronze'}
                </span>
              </div>
            </div>
            <p className="customer-email cdp-name-block" style={{ marginLeft: '0px' }}>{customer.email}</p>
            <p className="customer-phone cdp-name-block" style={{ marginLeft: '0px' }}>{customer.phoneNumber || 'No phone'}</p>
            <p className="customer-wallet-balance cdp-name-block" style={{ marginLeft: '0px' }}>
              Wallet Balance: {formatCurrency(customer.metrics?.walletBalance || 0)}
            </p>
            {customerPackages.filter(p => p.status === 'active').map(pkg => {
              const used = pkg.sessionsUsed || 0;
              const left = Math.max(0, (pkg.totalSessions || 0) - used);
              return (
                <p key={pkg.id} style={{ margin: '2px 0', fontSize: '13px', color: '#374151' }}>
                  <strong>{pkg.packageName}</strong> — Sessions Used: {used} | Sessions Left: {left}
                </p>
              );
            })}
            <div className="customer-stats cdp-stats-position" style={{ position: 'absolute', top: 0, marginLeft: '616px', width: '20%' }}>
              <span className="stat">
                <strong>{customer.metrics?.pointsBalance || 0}</strong> Points
              </span>
              <span className="stat">
                <strong>{customer.metrics?.totalVisits || 0}</strong> Visits
              </span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      <div className="customer-details-tabs">
        <button
          className={`tab-button ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          Appointment History
        </button>
        <button
          className={`tab-button ${activeTab === 'vouchers' ? 'active' : ''}`}
          onClick={() => setActiveTab('vouchers')}
        >
          Vouchers & Promotions
        </button>
        <button
          className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transaction History
        </button>
        <button
          className={`tab-button ${activeTab === 'packages' ? 'active' : ''}`}
          onClick={() => setActiveTab('packages')}
        >
          Package History
        </button>
      </div>

      <div className="customer-details-content">
        {tabLoading && <div className="loading-indicator">Loading {activeTab}...</div>}

        {activeTab === 'appointments' && (
          <div className="tab-content" style={{ display: 'block', minHeight: '200px' }}>
            <div className="transactions-header">
              <h3>Appointment History ({appointments.length} records)</h3>
              <button
                className="add-transaction-button"
                onClick={() => {
                  setAppointmentForm({ dateRequested: '', timeRequested: '', staffId: '', serviceId: '', packageId: '', appointmentType: 'service', notes: '', locationId: '' });
                  setAvailableTimeSlots([]);
                  setValidationMessage('');
                  setShowAddAppointment(true);
                }}
              >
                Add Appointment
              </button>
            </div>
            
            {appointments.length === 0 && !tabLoading ? (
              <div className="empty-state">
                <p>No appointments found for this customer.</p>
                <p><small>Appointment data will appear here when appointments are booked through the system.</small></p>
              </div>
            ) : (
              <div className="data-table-container" style={{ display: 'block', width: '100%' }}>
                <table className="data-table" style={{ display: 'table', width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Date Created</th>
                      <th>Date Requested</th>
                      <th>Service Name</th>
                      <th>Duration</th>
                      <th>Staff</th>
                      {isMultiLocation && <th>Location</th>}
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map(appointment => (
                      <tr key={appointment.id}>
                        <td>{formatDate(appointment.dateTimeCreated)}</td>
                        <td>{formatDate(appointment.dateTimeRequested)}</td>
                        <td>{appointment.serviceRequested?.name || 'N/A'}</td>
                        <td>{appointment.serviceRequested?.duration || 'N/A'} minutes</td>
                        <td>{appointment.staffName || 'N/A'}</td>
                        {isMultiLocation && (
                          <td>{locations.find(l => l.locationId === appointment.locationId)?.locationName || appointment.locationId || '—'}</td>
                        )}
                        <td>{getStatusBadge(appointment.status)}</td>
                        <td>{appointment.notes || 'N/A'}</td>
                        <td>
                          <button
                            className="delete-record-button"
                            onClick={() => handleDeleteAppointment(appointment.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'vouchers' && (
          <div className="tab-content" style={{ display: 'block', minHeight: '200px' }}>
            <h3>Vouchers & Promotions ({vouchers.length} records)</h3>
            
            {vouchers.length === 0 && !tabLoading ? (
              <div className="empty-state">
                <p>No vouchers and promotions found for this customer.</p>
                <p><small>Voucher data will appear here when vouchers are assigned through the system.</small></p>
              </div>
            ) : (
              <div className="data-table-container" style={{ display: 'block', width: '100%' }}>
                <table className="data-table" style={{ display: 'table', width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Promotion Name</th>
                      <th>Description</th>
                      <th>Voucher Code</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Redeemed Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.map(voucher => (
                      <tr key={voucher.id}>
                        <td>{voucher.promotionName || 'N/A'}</td>
                        <td className="description-cell">{voucher.description || 'N/A'}</td>
                        <td className="voucher-code">{voucher.voucherCode || 'N/A'}</td>
                        <td>{formatDate(voucher.startDate)}</td>
                        <td>{formatDate(voucher.endDate)}</td>
                        <td>{getStatusBadge(voucher.status)}</td>
                        <td>{voucher.redeemedAt ? formatDate(voucher.redeemedAt) : 'N/A'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {voucher.status?.toLowerCase() === 'active' ? (
                              <button
                                className="redeem-voucher-button"
                                onClick={() => handleRedeemVoucher(voucher)}
                                disabled={tabLoading}
                              >
                                {tabLoading ? 'Redeeming...' : 'Redeem'}
                              </button>
                            ) : (
                              <span className="no-action">-</span>
                            )}
                            <button
                              className="delete-record-button"
                              onClick={() => handleDeleteVoucher(voucher.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="tab-content" style={{ display: 'block', minHeight: '200px' }}>
            <div className="transactions-header">
              <h3>Transaction History ({transactions.length} records)</h3>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="add-transaction-button"
                  onClick={() => setShowTransactionTypeChoice(prev => !prev)}
                >
                  Add Transaction ▾
                </button>
                {showTransactionTypeChoice && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 200, minWidth: '210px', overflow: 'hidden'
                  }}>
                    <button
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '12px 16px', background: 'none', border: 'none',
                        fontSize: '14px', cursor: 'pointer', color: '#1f2937',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      onClick={() => {
                        setShowTransactionTypeChoice(false);
                        setShowAddTransaction(true);
                      }}
                    >
                      🪄 Add Service Transaction
                    </button>
                    <button
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '12px 16px', background: 'none', border: 'none',
                        fontSize: '14px', cursor: 'pointer', color: '#1f2937',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      onClick={() => {
                        setShowTransactionTypeChoice(false);
                        setShowAddProductTransaction(true);
                      }}
                    >
                      🛍️ Add Product Transaction
                    </button>
                    <button
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '12px 16px', background: 'none', border: 'none',
                        fontSize: '14px', cursor: 'pointer', color: '#1f2937'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      onClick={() => {
                        setShowTransactionTypeChoice(false);
                        setShowAddPackageTransaction(true);
                      }}
                    >
                      📦 Add Package Transaction
                    </button>
                  </div>
                )}
              </div>
            </div>

            {transactions.length === 0 && !tabLoading ? (
              <div className="empty-state">
                <p>No transactions found for this customer.</p>
                <p><small>Transaction data will appear here when transactions are made through the system.</small></p>
              </div>
            ) : (
              <div className="data-table-container" style={{ display: 'block', width: '100%' }}>
                <table className="data-table" style={{ display: 'table', width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Created Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Total Amount</th>
                      <th>Payment Method</th>
                      <th>Payment Status</th>
                      <th>Paid Date</th>
                      {isMultiLocation && <th>Location</th>}
                      <th>Points Earned</th>
                      <th>Points Used</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(transaction => (
                      <tr key={transaction.id}>
                        <td>{formatDate(transaction.createdAt)}</td>
                        <td>
                          {transaction.type === 'wallet_topup' ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px',
                              borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                              background: '#f5f3ff', color: '#6d28d9'
                            }}>Wallet Top-Up</span>
                          ) : transaction.type === 'product' ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px',
                              borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                              background: '#eff6ff', color: '#1d4ed8'
                            }}>Product</span>
                          ) : transaction.type === 'package' ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px',
                              borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                              background: '#fef3c7', color: '#92400e'
                            }}>Package</span>
                          ) : (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px',
                              borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                              background: '#f0fdf4', color: '#15803d'
                            }}>Service</span>
                          )}
                        </td>
                        <td>{formatCurrency(transaction.originalPrice ?? transaction.amount)}</td>
                        <td>{formatCurrency(transaction.price ?? transaction.totalAmount)}</td>
                        <td className="payment-method">{transaction.paymentMethod || 'N/A'}</td>
                        <td>{getStatusBadge(transaction.paymentStatus)}</td>
                        <td>{transaction.paidAt ? formatDate(transaction.paidAt) : 'N/A'}</td>
                        {isMultiLocation && (
                          <td>{locations.find(l => l.locationId === transaction.locationId)?.locationName || transaction.locationId || '—'}</td>
                        )}
                        <td>{transaction.pointsEarned || 0}</td>
                        <td>{transaction.pointsUsed || 0}</td>
                        <td>
                          {transaction.type === 'wallet_topup' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                              {transaction.creditAmount > transaction.amount && (
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#1a7a3c' }}>
                                  Credits {formatCurrency(transaction.creditAmount)}
                                </span>
                              )}
                              {transaction.proofImageUrl ? (
                                <a href={transaction.proofImageUrl} target="_blank" rel="noopener noreferrer">
                                  View Proof
                                </a>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '12px' }}>Awaiting proof upload</span>
                              )}
                              {transaction.walletTopupStatus === 'pending' ? (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    className="edit-transaction-button"
                                    disabled={walletActionLoadingId === transaction.id}
                                    onClick={() => handleVerifyTopup(transaction)}
                                  >
                                    {walletActionLoadingId === transaction.id ? 'Working...' : 'Verify'}
                                  </button>
                                  <button
                                    className="delete-record-button"
                                    disabled={walletActionLoadingId === transaction.id}
                                    onClick={() => handleRejectTopup(transaction)}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : (
                                getStatusBadge(transaction.paymentStatus)
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                className="edit-transaction-button"
                                onClick={() => handleEditTransaction(transaction)}
                              >
                                Edit Transaction
                              </button>
                              <button
                                className="delete-record-button"
                                onClick={() => handleDeleteTransaction(transaction.id)}
                              >
                                Delete Transaction
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Package History Tab ── */}
        {activeTab === 'packages' && (
          <div className="tab-content" style={{ display: 'block', minHeight: '200px' }}>
            <div className="transactions-header">
              <h3>Package History ({packageHistory.length} records)</h3>
              <button
                className="add-transaction-button"
                onClick={() => {
                  setPackageActionForm({ packageId: '', action: '' });
                  setShowAddPackageAction(true);
                }}
              >
                Add Package Action
              </button>
            </div>

            {packageHistory.length === 0 && !tabLoading ? (
              <div className="empty-state">
                <p>No package actions found for this customer.</p>
                <p><small>Actions will appear here once packages are added or modified.</small></p>
              </div>
            ) : (
              <div className="data-table-container" style={{ display: 'block', width: '100%' }}>
                <table className="data-table" style={{ display: 'table', width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Package Name</th>
                      <th>Total Sessions</th>
                      <th>Sessions Used</th>
                      <th>Sessions Left</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packageHistory.map(entry => (
                      <tr key={entry.id}>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px',
                            borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                            background: entry.action === 'Add Package' ? '#eff6ff'
                              : entry.action === 'Delete Package' ? '#fef2f2'
                              : entry.action === 'Deplete Session' ? '#fff7ed'
                              : '#f0fdf4',
                            color: entry.action === 'Add Package' ? '#1d4ed8'
                              : entry.action === 'Delete Package' ? '#dc2626'
                              : entry.action === 'Deplete Session' ? '#c2410c'
                              : '#15803d',
                          }}>
                            {entry.action}
                          </span>
                        </td>
                        <td>{entry.packageName || '—'}</td>
                        <td>{entry.totalSessionsAfter ?? '—'}</td>
                        <td>{entry.sessionsUsedAfter ?? 0}</td>
                        <td>{entry.sessionsLeftAfter ?? '—'}</td>
                        <td>{entry.performedAt?.toDate ? entry.performedAt.toDate().toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Package Action Modal */}
      {showAddPackageAction && (
        <div className="modal-overlay" onClick={() => setShowAddPackageAction(false)}>
          <div className="add-transaction-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Package Action</h3>
              <button onClick={() => setShowAddPackageAction(false)}>×</button>
            </div>
            <div className="modal-content">

              <div className="form-group">
                <label>Package Action *</label>
                <select
                  value={packageActionForm.action}
                  onChange={e => setPackageActionForm(f => ({ ...f, action: e.target.value, packageId: '' }))}
                >
                  <option value="">— Select action —</option>
                  <option value="Add Package">Add Package</option>
                  <option value="Delete Package">Delete Package</option>
                  <option value="Add Session">Add Session</option>
                  <option value="Deplete Session">Deplete Session</option>
                </select>
              </div>

              {packageActionForm.action === 'Add Package' && (
                <div className="form-group">
                  <label>Package Name *</label>
                  <select
                    value={packageActionForm.packageId}
                    onChange={e => setPackageActionForm(f => ({ ...f, packageId: e.target.value }))}
                  >
                    <option value="">— Select package —</option>
                    {salonServicePackages.map((p, i) => (
                      <option key={i} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                  {salonServicePackages.length === 0 && (
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      No service packages configured yet. Set them up under Core → Packages.
                    </p>
                  )}
                </div>
              )}

              {(packageActionForm.action === 'Delete Package' ||
                packageActionForm.action === 'Add Session' ||
                packageActionForm.action === 'Deplete Session') && (
                <div className="form-group">
                  <label>Select Package *</label>
                  <select
                    value={packageActionForm.packageId}
                    onChange={e => setPackageActionForm(f => ({ ...f, packageId: e.target.value }))}
                  >
                    <option value="">— Select customer package —</option>
                    {customerPackages.filter(p => p.status === 'active').map(p => (
                      <option key={p.id} value={p.id}>
                        {p.packageName} — {Math.max(0, (p.totalSessions || 0) - (p.sessionsUsed || 0))} sessions left
                      </option>
                    ))}
                  </select>
                  {customerPackages.filter(p => p.status === 'active').length === 0 && (
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      No active packages for this customer.
                    </p>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="add-transaction-button"
                  disabled={packageActionSaving || !packageActionForm.action || !packageActionForm.packageId}
                  onClick={handlePackageAction}
                >
                  {packageActionSaving ? 'Saving...' : 'Confirm'}
                </button>
                <button
                  className="cancel-button"
                  onClick={() => setShowAddPackageAction(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Appointment Modal */}
      {showAddAppointment && (
        <div className="modal-overlay" onClick={() => setShowAddAppointment(false)}>
          <div className="add-transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Appointment</h3>
              <button onClick={() => setShowAddAppointment(false)}>×</button>
            </div>
            <div className="modal-content">
              {isMultiLocation && (
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={appointmentForm.locationId}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, locationId: e.target.value, staffId: '', serviceId: '', packageId: '' })}
                  >
                    <option value="">-- Select Location --</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>{loc.locationName}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Appointment type toggle */}
              <div className="form-group">
                <label>Appointment Type</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="service"
                      checked={appointmentForm.appointmentType === 'service'}
                      onChange={() => setAppointmentForm({ ...appointmentForm, appointmentType: 'service', packageId: '', staffId: '', serviceId: '', dateRequested: '', timeRequested: '' })}
                    />
                    Service
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="package"
                      checked={appointmentForm.appointmentType === 'package'}
                      onChange={() => setAppointmentForm({ ...appointmentForm, appointmentType: 'package', serviceId: '', staffId: '', packageId: '', dateRequested: '', timeRequested: '' })}
                    />
                    Package
                  </label>
                </div>
              </div>

              {/* ── SERVICE PATH ── */}
              {appointmentForm.appointmentType === 'service' && (
                <>
                  <div className="form-group">
                    <label>Staff Name</label>
                    <select
                      value={appointmentForm.staffId}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, staffId: e.target.value, serviceId: '', dateRequested: '', timeRequested: '' })}
                      required
                    >
                      <option value="">Select a staff</option>
                      {(appointmentForm.locationId
                        ? staff.filter(s =>
                            (s.locationIds && s.locationIds.includes(appointmentForm.locationId)) ||
                            s.locationId === appointmentForm.locationId ||
                            (!s.locationIds && !s.locationId)
                          )
                        : staff
                      ).map(staffMember => (
                        <option key={staffMember.id} value={staffMember.id}>
                          {staffMember.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Service Name</label>
                    <select
                      value={appointmentForm.serviceId}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, serviceId: e.target.value })}
                      required
                      disabled={!appointmentForm.staffId}
                    >
                      <option value="">
                        {!appointmentForm.staffId ? 'Select a staff first' : 'Select a service'}
                      </option>
                      {filteredServices.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name} - {formatServicePriceRange(service)} ({service.duration || '60'} mins)
                        </option>
                      ))}
                    </select>
                    {appointmentForm.staffId && filteredServices.length === 0 && (
                      <span className="info-text" style={{color: '#999', fontSize: '13px', marginTop: '5px', display: 'block'}}>
                        No services available for selected staff member
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* ── PACKAGE PATH ── */}
              {appointmentForm.appointmentType === 'package' && (() => {
                const activeCustomerPackages = customerPackages.filter(p => p.status === 'active');
                const selectedCustPkg = customerPackages.find(p => p.id === appointmentForm.packageId);
                const pkgStaffIds = selectedCustPkg?.packageDefinition?.staffIds || [];
                const eligibleStaff = pkgStaffIds.length > 0
                  ? staff.filter(s => pkgStaffIds.includes(s.id))
                  : staff;
                const locationFilteredEligibleStaff = appointmentForm.locationId
                  ? eligibleStaff.filter(s =>
                      (s.locationIds && s.locationIds.includes(appointmentForm.locationId)) ||
                      s.locationId === appointmentForm.locationId ||
                      (!s.locationIds && !s.locationId)
                    )
                  : eligibleStaff;

                return (
                  <>
                    <div className="form-group">
                      <label>Package</label>
                      <select
                        value={appointmentForm.packageId}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, packageId: e.target.value, staffId: '', dateRequested: '', timeRequested: '' })}
                        required
                      >
                        <option value="">
                          {activeCustomerPackages.length === 0 ? 'No active packages for this customer' : 'Select a package'}
                        </option>
                        {activeCustomerPackages.map(p => {
                          const left = Math.max(0, (p.totalSessions || 0) - (p.sessionsUsed || 0));
                          return (
                            <option key={p.id} value={p.id}>
                              {p.packageName} — {left} session{left !== 1 ? 's' : ''} left
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Staff Name</label>
                      <select
                        value={appointmentForm.staffId}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, staffId: e.target.value, dateRequested: '', timeRequested: '' })}
                        required
                        disabled={!appointmentForm.packageId}
                      >
                        <option value="">
                          {!appointmentForm.packageId ? 'Select a package first' : 'Select a staff'}
                        </option>
                        {locationFilteredEligibleStaff.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      {appointmentForm.packageId && locationFilteredEligibleStaff.length === 0 && (
                        <span className="info-text" style={{color: '#999', fontSize: '13px', marginTop: '5px', display: 'block'}}>
                          No staff available for this package
                        </span>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Date + Time — shared by both paths, shown after staff is chosen */}
              <div className="form-group">
                <label>Date Requested</label>
                <input
                  type="date"
                  value={appointmentForm.dateRequested}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, dateRequested: e.target.value, timeRequested: '' })}
                  disabled={!appointmentForm.staffId}
                  required
                />
              </div>

              <div className="form-group">
                <label>Time Requested</label>
                <select
                  value={appointmentForm.timeRequested}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, timeRequested: e.target.value })}
                  required
                  disabled={!appointmentForm.dateRequested || !appointmentForm.staffId}
                >
                  <option value="">
                    {!appointmentForm.staffId
                      ? 'Select a staff first'
                      : !appointmentForm.dateRequested
                        ? 'Select a date first'
                        : 'Select a time'}
                  </option>
                  {availableTimeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
                {validationMessage && (
                  <span className="info-text" style={{color: '#ff9800', fontSize: '13px', marginTop: '5px', display: 'block'}}>
                    {validationMessage}
                  </span>
                )}
                {appointmentForm.dateRequested && appointmentForm.staffId && availableTimeSlots.length === 0 && !validationMessage && (
                  <span className="info-text" style={{color: '#999', fontSize: '13px', marginTop: '5px', display: 'block'}}>
                    Loading available times...
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  rows="3"
                  placeholder="Enter any additional notes..."
                  value={appointmentForm.notes}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowAddAppointment(false)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAppointment}
                className="save-button"
                disabled={tabLoading}
              >
                {tabLoading ? 'Adding...' : 'Add Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Service Transaction Modal */}
      {showAddTransaction && (
        <div className="modal-overlay" onClick={handleCloseAddTransaction}>
          <div className="add-transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Service Transaction</h3>
              <button onClick={handleCloseAddTransaction}>×</button>
            </div>
            <div className="modal-content">

              {/* Voucher banner — shown when admin arrives via QR scan */}
              {activeVoucher && (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #86efac',
                  borderRadius: '8px', padding: '12px 14px', marginBottom: '14px'
                }}>
                  <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: '13px', color: '#15803d' }}>
                    🎫 Voucher Loaded: {activeVoucher.promotionName}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#166534' }}>
                    {activeVoucher.type === 'percentage'   && `${activeVoucher.discountValue}% off`}
                    {activeVoucher.type === 'fixed_amount' && `$${activeVoucher.discountValue} off`}
                    {activeVoucher.type === 'points_based' && `$${activeVoucher.voucherAmount} credit · ${(activeVoucher.pointsRequired || 0).toLocaleString()} points will be deducted on completion`}
                    {activeVoucher.type === 'free_gift'    && (activeVoucher.freeGiftDescription || 'Free Gift')}
                  </p>
                </div>
              )}

              {isMultiLocation && (
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={transactionForm.locationId}
                    onChange={(e) => setTransactionForm({ ...transactionForm, locationId: e.target.value, serviceId: '', staffId: '' })}
                  >
                    <option value="">-- Select Location --</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>{loc.locationName}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Service Name</label>
                <select
                  value={transactionForm.serviceId}
                  onChange={(e) => setTransactionForm({
                    ...transactionForm,
                    serviceId: e.target.value,
                    staffId: '' // Reset staff when service changes
                  })}
                >
                  <option value="">Select a service</option>
                  {(transactionForm.locationId
                    ? (() => {
                        // Derive available services from staff assigned to the selected location
                        const staffAtLoc = staff.filter(s =>
                          (s.locationIds && s.locationIds.includes(transactionForm.locationId)) ||
                          s.locationId === transactionForm.locationId ||
                          (!s.locationIds && !s.locationId)
                        );
                        const serviceIdSet = new Set();
                        staffAtLoc.forEach(s => (s.services || []).forEach(id => serviceIdSet.add(id)));
                        return services.filter(sv =>
                          serviceIdSet.has(sv.id) || serviceIdSet.has(sv.serviceId)
                        );
                      })()
                    : services
                  ).map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - {formatServicePriceRange(service)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Staff Name</label>
                <select
                  value={transactionForm.staffId}
                  onChange={(e) => setTransactionForm({
                    ...transactionForm,
                    staffId: e.target.value
                  })}
                >
                  <option value="">Select a staff</option>
                  {(() => {
                    const locationStaff = transactionForm.locationId
                      ? staff.filter(s =>
                          (s.locationIds && s.locationIds.includes(transactionForm.locationId)) ||
                          s.locationId === transactionForm.locationId ||
                          (!s.locationIds && !s.locationId)
                        )
                      : staff;
                    const selectedService = services.find(s => s.id === transactionForm.serviceId);
                    const selectedServiceName = selectedService?.name || '';
                    const selectedServiceUUID = selectedService?.serviceId || selectedService?.id || '';
                    const filteredStaff = transactionForm.serviceId
                      ? locationStaff.filter(staffMember => {
                          if (!staffMember.services || !Array.isArray(staffMember.services)) return false;
                          return staffMember.services.some(svc => {
                            if (typeof svc === 'string') return svc === selectedServiceUUID || svc === selectedServiceName || svc === transactionForm.serviceId;
                            if (svc && typeof svc === 'object') return svc.serviceId === selectedServiceUUID || svc.name === selectedServiceName || svc.id === transactionForm.serviceId;
                            return false;
                          });
                        })
                      : locationStaff;
                    return filteredStaff.map(staffMember => (
                      <option key={staffMember.id} value={staffMember.id}>
                        {staffMember.name}
                      </option>
                    ));
                  })()}
                </select>
              </div>
              
              <div className="form-group">
                <label>Custom Price</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Enter price for this service"
                    value={transactionForm.customPrice}
                    onChange={(e) => {
                      setTransactionForm({ ...transactionForm, customPrice: e.target.value });
                      // Reset applied discount if price is changed after voucher was applied
                      if (voucherApplied) {
                        setVoucherApplied(false);
                        setNettPrice(null);
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  {/* Redeem Voucher button — only shown when a voucher is loaded */}
                  {activeVoucher && (
                    <button
                      type="button"
                      onClick={handleApplyVoucher}
                      disabled={voucherApplied}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '6px',
                        border: 'none',
                        background: voucherApplied ? '#86efac' : '#15803d',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: voucherApplied ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      {voucherApplied ? '✓ Applied' : 'Redeem Voucher'}
                    </button>
                  )}
                </div>
              </div>

              {/* Nett Price — read-only, shown once voucher is applied */}
              {activeVoucher && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Nett Price
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
                      (after voucher discount)
                    </span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={voucherApplied && nettPrice !== null
                      ? `$${nettPrice.toFixed(2)}`
                      : '— enter Custom Price then click Redeem Voucher'}
                    style={{
                      background: '#f9fafb',
                      color: voucherApplied ? '#15803d' : '#aaa',
                      fontWeight: voucherApplied ? 600 : 400,
                      cursor: 'default',
                      border: '1px solid #e5e7eb'
                    }}
                  />
                </div>
              )}
              
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={transactionForm.paymentMethod}
                  onChange={(e) => setTransactionForm({
                    ...transactionForm,
                    paymentMethod: e.target.value
                  })}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="paynow">PayNow</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option
                    value="Points"
                    disabled={!canRedeemWithPoints()}
                  >
                    Points{!canRedeemWithPoints() && transactionForm.serviceId ? ' (Insufficient balance)' : ''}
                  </option>
                  <option
                    value="Wallet"
                    disabled={!canPayWithWallet()}
                  >
                    Wallet{!canPayWithWallet() && transactionForm.serviceId ? ' (Insufficient balance)' : ''}
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Status</label>
                <select
                  value={transactionForm.paymentStatus}
                  onChange={(e) => setTransactionForm({
                    ...transactionForm,
                    paymentStatus: e.target.value
                  })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              {/* Points redemption info */}
              {transactionForm.paymentMethod === 'Points' && transactionForm.serviceId && (
                <div className="points-redemption-info">
                  <div className="points-required">
                    <span className="points-label">Points Required:</span>
                    <span className="points-value">{getPointsRequired().toLocaleString()}</span>
                  </div>
                  <div className="points-available">
                    <span className="points-label">Points Available:</span>
                    <span className="points-value">{(customer?.metrics?.pointsBalance || 0).toLocaleString()}</span>
                  </div>
                  {!canRedeemWithPoints() && (
                    <div className="points-insufficient-warning">
                      Customer does not have enough points.
                      Required: {getPointsRequired().toLocaleString()} points.
                      Available: {(customer?.metrics?.pointsBalance || 0).toLocaleString()} points.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                onClick={handleCloseAddTransaction}
                className="cancel-button"
              >
                Cancel
              </button>
              {paynowConfig && paynowConfig.isActive && transactionForm.paymentMethod === 'paynow' && (
                <button
                  onClick={handleGeneratePayNowQR}
                  className="paynow-button"
                  disabled={tabLoading}
                >
                  {tabLoading ? 'Generating...' : 'Generate QR Code'}
                </button>
              )}
              <button
                onClick={handleAddTransaction}
                className="save-button"
                disabled={
                  tabLoading ||
                  (transactionForm.paymentMethod === 'Points' && !canRedeemWithPoints()) ||
                  (transactionForm.paymentMethod === 'Wallet' && !canPayWithWallet()) ||
                  // If a discount voucher is loaded but not yet applied, block submission
                  (activeVoucher && ['percentage','fixed_amount','points_based'].includes(activeVoucher.type) && !voucherApplied)
                }
                title={
                  activeVoucher && ['percentage','fixed_amount','points_based'].includes(activeVoucher.type) && !voucherApplied
                    ? 'Enter a Custom Price and click "Redeem Voucher" to apply the discount first'
                    : ''
                }
              >
                {tabLoading ? 'Adding...' : 'Add Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Transaction Modal */}
      {showAddProductTransaction && (
        <div className="modal-overlay" onClick={handleCloseProductTransaction}>
          <div className="add-transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Product Transaction</h3>
              <button onClick={handleCloseProductTransaction}>×</button>
            </div>
            <div className="modal-content">

              {/* Voucher banner */}
              {activeVoucher && (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #86efac',
                  borderRadius: '8px', padding: '12px 14px', marginBottom: '14px'
                }}>
                  <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: '13px', color: '#15803d' }}>
                    🎫 Voucher Loaded: {activeVoucher.promotionName}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#166534' }}>
                    {activeVoucher.type === 'percentage'   && `${activeVoucher.discountValue}% off`}
                    {activeVoucher.type === 'fixed_amount' && `$${activeVoucher.discountValue} off`}
                    {activeVoucher.type === 'points_based' && `$${activeVoucher.voucherAmount} credit · ${(activeVoucher.pointsRequired || 0).toLocaleString()} points will be deducted on completion`}
                    {activeVoucher.type === 'free_gift'    && (activeVoucher.freeGiftDescription || 'Free Gift')}
                  </p>
                </div>
              )}

              {/* Location — multi-location only */}
              {isMultiLocation && (
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={productTransactionForm.locationId}
                    onChange={(e) => setProductTransactionForm(prev => ({
                      ...prev, locationId: e.target.value, productId: '', staffId: ''
                    }))}
                  >
                    <option value="">-- Select Location --</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>{loc.locationName}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Product Name */}
              <div className="form-group">
                <label>Product Name</label>
                <select
                  value={productTransactionForm.productId}
                  onChange={(e) => {
                    const prod = products.find(p => p.id === e.target.value);
                    setProductTransactionForm(prev => ({
                      ...prev,
                      productId:   e.target.value,
                      customPrice: prod ? String(prod.price || '') : ''
                    }));
                    if (voucherApplied) { setVoucherApplied(false); setNettPrice(null); }
                  }}
                >
                  <option value="">Select a product</option>
                  {getProductsForLocation(productTransactionForm.locationId).map(prod => (
                    <option key={prod.id} value={prod.id}>
                      {prod.name}{prod.price ? ` — $${Number(prod.price).toFixed(2)}` : ''}
                      {prod.stockLevel !== undefined && prod.stockLevel !== null ? ` (Stock: ${prod.stockLevel})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={productTransactionForm.quantity}
                  onChange={(e) => setProductTransactionForm(prev => ({
                    ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1)
                  }))}
                  style={{ width: '100px' }}
                />
              </div>

              {/* Staff Name */}
              <div className="form-group">
                <label>Staff Name</label>
                <select
                  value={productTransactionForm.staffId}
                  onChange={(e) => setProductTransactionForm(prev => ({ ...prev, staffId: e.target.value }))}
                >
                  <option value="">Select a staff member</option>
                  {(productTransactionForm.locationId
                    ? staff.filter(s =>
                        (s.locationIds && s.locationIds.includes(productTransactionForm.locationId)) ||
                        s.locationId === productTransactionForm.locationId ||
                        (!s.locationIds && !s.locationId)
                      )
                    : staff
                  ).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Custom Price */}
              <div className="form-group">
                <label>Custom Price</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Enter price for this product"
                    value={productTransactionForm.customPrice}
                    onChange={(e) => {
                      setProductTransactionForm(prev => ({ ...prev, customPrice: e.target.value }));
                      if (voucherApplied) { setVoucherApplied(false); setNettPrice(null); }
                    }}
                    style={{ flex: 1 }}
                  />
                  {activeVoucher && (
                    <button
                      type="button"
                      onClick={handleApplyVoucher}
                      disabled={voucherApplied}
                      style={{
                        padding: '8px 14px', borderRadius: '6px', border: 'none',
                        background: voucherApplied ? '#86efac' : '#15803d',
                        color: '#fff', fontSize: '13px', fontWeight: 600,
                        cursor: voucherApplied ? 'default' : 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0
                      }}
                    >
                      {voucherApplied ? '✓ Applied' : 'Redeem Voucher'}
                    </button>
                  )}
                </div>
              </div>

              {/* Nett Price after voucher */}
              {activeVoucher && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Nett Price
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>(after voucher discount)</span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={voucherApplied && nettPrice !== null
                      ? `$${nettPrice.toFixed(2)}`
                      : '— enter Custom Price then click Redeem Voucher'}
                    style={{
                      background: '#f9fafb',
                      color: voucherApplied ? '#15803d' : '#aaa',
                      fontWeight: voucherApplied ? 600 : 400,
                      cursor: 'default', border: '1px solid #e5e7eb'
                    }}
                  />
                </div>
              )}

              {/* Payment Method */}
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={productTransactionForm.paymentMethod}
                  onChange={(e) => setProductTransactionForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="paynow">PayNow</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option
                    value="Points"
                    disabled={!canRedeemProductWithPoints()}
                  >
                    Points{!canRedeemProductWithPoints() && productTransactionForm.productId ? ' (Insufficient balance)' : ''}
                  </option>
                  <option
                    value="Wallet"
                    disabled={!canPayProductWithWallet()}
                  >
                    Wallet{!canPayProductWithWallet() && productTransactionForm.productId ? ' (Insufficient balance)' : ''}
                  </option>
                </select>
              </div>

              {/* Payment Status */}
              <div className="form-group">
                <label>Payment Status</label>
                <select
                  value={productTransactionForm.paymentStatus}
                  onChange={(e) => setProductTransactionForm(prev => ({ ...prev, paymentStatus: e.target.value }))}
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              {/* Points redemption info */}
              {productTransactionForm.paymentMethod === 'Points' && productTransactionForm.productId && (
                <div className="points-redemption-info">
                  <div className="points-required">
                    <span className="points-label">Points Required:</span>
                    <span className="points-value">{getProductPointsRequired().toLocaleString()}</span>
                  </div>
                  <div className="points-available">
                    <span className="points-label">Points Available:</span>
                    <span className="points-value">{(customer?.metrics?.pointsBalance || 0).toLocaleString()}</span>
                  </div>
                  {!canRedeemProductWithPoints() && (
                    <div className="points-insufficient-warning">
                      Customer does not have enough points.
                      Required: {getProductPointsRequired().toLocaleString()} points.
                      Available: {(customer?.metrics?.pointsBalance || 0).toLocaleString()} points.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button onClick={handleCloseProductTransaction} className="cancel-button">
                Cancel
              </button>
              {paynowConfig && paynowConfig.isActive && productTransactionForm.paymentMethod === 'paynow' && (
                <button
                  onClick={handleGenerateProductPayNowQR}
                  className="paynow-button"
                  disabled={tabLoading}
                >
                  {tabLoading ? 'Generating...' : 'Generate QR Code'}
                </button>
              )}
              <button
                onClick={handleAddProductTransaction}
                className="save-button"
                disabled={
                  tabLoading ||
                  (productTransactionForm.paymentMethod === 'Points' && !canRedeemProductWithPoints()) ||
                  (productTransactionForm.paymentMethod === 'Wallet' && !canPayProductWithWallet()) ||
                  (activeVoucher && ['percentage','fixed_amount','points_based'].includes(activeVoucher.type) && !voucherApplied)
                }
                title={
                  activeVoucher && ['percentage','fixed_amount','points_based'].includes(activeVoucher.type) && !voucherApplied
                    ? 'Enter a Custom Price and click "Redeem Voucher" to apply the discount first'
                    : ''
                }
              >
                {tabLoading ? 'Adding...' : 'Add Product Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Package Transaction Modal */}
      {showAddPackageTransaction && (
        <div className="modal-overlay" onClick={handleClosePackageTransaction}>
          <div className="add-transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Package Transaction</h3>
              <button onClick={handleClosePackageTransaction}>×</button>
            </div>
            <div className="modal-content">

              {/* Location — multi-location only */}
              {isMultiLocation && (
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={packageTransactionForm.locationId}
                    onChange={(e) => setPackageTransactionForm(prev => ({
                      ...prev, locationId: e.target.value, staffId: ''
                    }))}
                  >
                    <option value="">-- Select Location --</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>{loc.locationName}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Package Name */}
              <div className="form-group">
                <label>Package Name</label>
                <select
                  value={packageTransactionForm.packageName}
                  onChange={(e) => setPackageTransactionForm(prev => ({
                    ...prev, packageName: e.target.value
                  }))}
                >
                  <option value="">Select a package</option>
                  {salonServicePackages.map((pkg, idx) => (
                    <option key={idx} value={pkg.name}>
                      {pkg.name}{pkg.price ? ` — $${Number(pkg.price).toFixed(2)}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={packageTransactionForm.quantity}
                  onChange={(e) => setPackageTransactionForm(prev => ({
                    ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1)
                  }))}
                  style={{ width: '100px' }}
                />
              </div>

              {/* Staff Name */}
              <div className="form-group">
                <label>Staff Name</label>
                <select
                  value={packageTransactionForm.staffId}
                  onChange={(e) => setPackageTransactionForm(prev => ({ ...prev, staffId: e.target.value }))}
                >
                  <option value="">Select a staff member</option>
                  {(packageTransactionForm.locationId
                    ? staff.filter(s =>
                        (s.locationIds && s.locationIds.includes(packageTransactionForm.locationId)) ||
                        s.locationId === packageTransactionForm.locationId ||
                        (!s.locationIds && !s.locationId)
                      )
                    : staff
                  ).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Custom Price */}
              <div className="form-group">
                <label>Custom Price</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Enter price for this package"
                  value={packageTransactionForm.customPrice}
                  onChange={(e) => setPackageTransactionForm(prev => ({ ...prev, customPrice: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>

              {/* Payment Method */}
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={packageTransactionForm.paymentMethod}
                  onChange={(e) => setPackageTransactionForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="paynow">PayNow</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="Points">Points</option>
                  <option value="Wallet">Wallet</option>
                </select>
              </div>

              {/* Payment Status */}
              <div className="form-group">
                <label>Payment Status</label>
                <select
                  value={packageTransactionForm.paymentStatus}
                  onChange={(e) => setPackageTransactionForm(prev => ({ ...prev, paymentStatus: e.target.value }))}
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

            </div>

            <div className="modal-actions">
              <button onClick={handleClosePackageTransaction} className="cancel-button">
                Cancel
              </button>
              <button
                onClick={handleAddPackageTransaction}
                className="save-button"
                disabled={tabLoading}
              >
                {tabLoading ? 'Adding...' : 'Add Package Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {showEditTransaction && editingTransaction && (
        <div className="modal-overlay" onClick={() => setShowEditTransaction(false)}>
          <div className="add-transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Transaction</h3>
              <button onClick={() => setShowEditTransaction(false)}>×</button>
            </div>
            <div className="modal-content">
              {editingTransaction.type === 'product' ? (
                <>
                  <div className="form-group">
                    <label>Product Name</label>
                    <select
                      value={editingTransaction.productId}
                      onChange={(e) => {
                        const prod = products.find(p => p.id === e.target.value);
                        setEditingTransaction({
                          ...editingTransaction,
                          productId: e.target.value,
                          customPrice: prod ? String(prod.price || '') : editingTransaction.customPrice
                        });
                      }}
                    >
                      <option value="">Select a product</option>
                      {products.map(prod => (
                        <option key={prod.id} value={prod.id}>
                          {prod.name}{prod.price ? ` — $${Number(prod.price).toFixed(2)}` : ''}
                          {prod.stockLevel !== undefined && prod.stockLevel !== null ? ` (Stock: ${prod.stockLevel})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={editingTransaction.quantity}
                      onChange={(e) => setEditingTransaction({
                        ...editingTransaction,
                        quantity: Math.max(1, parseInt(e.target.value) || 1)
                      })}
                      style={{ width: '100px' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Staff Name</label>
                    <select
                      value={editingTransaction.staffId}
                      onChange={(e) => setEditingTransaction({ ...editingTransaction, staffId: e.target.value })}
                    >
                      <option value="">Select a staff member</option>
                      {staff.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Service Name</label>
                    <select
                      value={editingTransaction.serviceId}
                      onChange={(e) => setEditingTransaction({
                        ...editingTransaction,
                        serviceId: e.target.value,
                        staffId: ''
                      })}
                    >
                      <option value="">Select a service</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name} - {formatServicePriceRange(service)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Staff Name</label>
                    <select
                      value={editingTransaction.staffId}
                      onChange={(e) => setEditingTransaction({
                        ...editingTransaction,
                        staffId: e.target.value
                      })}
                    >
                      <option value="">Select a staff</option>
                      {(() => {
                        const selectedService = services.find(s => s.id === editingTransaction.serviceId);
                        const selectedServiceName = selectedService?.name || '';
                        const selectedServiceUUID = selectedService?.serviceId || '';
                        const filteredStaff = editingTransaction.serviceId
                          ? staff.filter(staffMember => {
                              if (!staffMember.services || !Array.isArray(staffMember.services)) return false;
                              return staffMember.services.some(svc => {
                                if (typeof svc === 'string') return svc === selectedServiceUUID || svc === selectedServiceName || svc === editingTransaction.serviceId;
                                if (svc && typeof svc === 'object') return svc.serviceId === selectedServiceUUID || svc.name === selectedServiceName || svc.id === editingTransaction.serviceId;
                                return false;
                              });
                            })
                          : staff;
                        return filteredStaff.map(staffMember => (
                          <option key={staffMember.id} value={staffMember.id}>
                            {staffMember.name}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                </>
              )}
              
              <div className="form-group">
                <label>Custom Price</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Enter price"
                  value={editingTransaction.customPrice}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    customPrice: e.target.value
                  })}
                />
              </div>
              
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={editingTransaction.paymentMethod}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    paymentMethod: e.target.value
                  })}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="paynow">PayNow</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="Points">Points</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Status</label>
                <select
                  value={editingTransaction.paymentStatus}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    paymentStatus: e.target.value
                  })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowEditTransaction(false)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTransaction}
                className="save-button"
                disabled={tabLoading}
              >
                {tabLoading ? 'Updating...' : 'Update Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PayNow QR Code Modal */}
      <PayNowQRModal
        isOpen={showPayNowQR}
        onClose={() => {
          setShowPayNowQR(false);
          setPendingTransactionId(null);
          setPaynowReference(null);
          setPaynowQRData(null);
          setPaynowIsDynamic(false);
          setPaynowFallback(false);
          setPaynowFallbackReason('');
          setPaynowIsGenerating(false);
          setPendingProductId(null);
          setPendingProductQuantity(1);
        }}
        qrCodeData={paynowQRData}
        qrCodeUrl={paynowConfig?.qrCodeImageUrl}
        isDynamic={paynowIsDynamic}
        fallback={paynowFallback}
        fallbackReason={paynowFallbackReason}
        amount={paynowAmount}
        reference={paynowReference}
        transactionId={pendingTransactionId}
        salonId={salonId}
        onPaymentVerified={handlePayNowVerified}
        onTryAgain={handleTryAgain}
        onCancelTransaction={handleCancelTransaction}
        isGenerating={paynowIsGenerating}
      />
    </div>
  );
}

export default CustomerDetailsPage;